import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getSettings, updateSettings, getAllCMS, updateCMS,
  getAnimals, getAnimal, createAnimal, updateAnimal, deleteAnimal, uploadAnimalPhoto, deleteAnimalPhoto,
  getMedia, uploadMedia, uploadMediaWithProgress, deleteMedia, toggleMediaVisibility,
  getReviews, updateReview, deleteReview, createReviewAdmin, uploadReviewPhoto,
  getAffiliates, createAffiliate, updateAffiliate, deleteAffiliate,
  getInquiries, updateInquiry, deleteInquiry,
  getLitters, createLitter, updateLitter, deleteLitter, uploadLitterPhoto, deleteLitterPhoto,
  getKittens, createKitten, updateKitten, deleteKitten, uploadKittenPhoto,
  getBuyers, updateBuyerProfile,
  deleteUser, adminCreateUser, adminResetPassword,
  getContracts, createContract, updateContractFull, deleteContract, approveContract, adminSignContract,
  getContractTemplates,
  getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement, uploadAnnouncementMedia,
  createKnowledgeChapter, deleteKnowledgeChapter, uploadKnowledgeImage, deleteKnowledgeImage
} from '../api';
import {
  Settings, FileText, Cat, Image, Star, Link2, LogOut, LayoutDashboard,
  Plus, Trash2, Edit, Eye, EyeOff, Save, Upload, X, Check, AlertCircle,
  ChevronDown, Menu, Search, MessageSquare, Users, Baby, CreditCard,
  Palette, Type, Monitor, Megaphone
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminPanel = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: LayoutDashboard },
    { id: 'settings', label: 'Einstellungen', icon: Settings },
    { id: 'cms', label: 'CMS-Inhalte', icon: FileText },
    { id: 'animals', label: 'Tiere', icon: Cat },
    { id: 'inquiries', label: 'Anfragen', icon: MessageSquare },
    { id: 'media', label: 'Medien & Galerie', icon: Image },
    { id: 'reviews', label: 'Bewertungen', icon: Star },
    { id: 'affiliates', label: 'Affiliate-Links', icon: Link2 },
    { id: 'announcements', label: 'Neuigkeiten', icon: Megaphone },
    { id: 'litters', label: 'Würfe', icon: Baby },
    { id: 'contracts', label: 'Verträge', icon: FileText },
    { id: 'buyers', label: 'Interessenten & Käufer', icon: CreditCard },
    { id: 'preview', label: 'Dashboard-Vorschau', icon: Monitor },
  ];

  return (
    <div className="min-h-screen bg-[#EEF3F8]">
      {/* Mobile top bar */}
      <div className="lg:hidden bg-[#2E3A46] text-white px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1" data-testid="admin-mobile-menu">
          <Menu size={22} />
        </button>
        <span className="font-heading text-lg">Admin</span>
        <button onClick={() => { onLogout(); navigate('/'); }} className="p-1">
          <LogOut size={18} />
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          data-testid="admin-sidebar"
          className={`admin-sidebar w-64 shrink-0 fixed lg:sticky top-0 h-screen z-40 transition-transform lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-5 border-b border-[#354551]">
            <a href="/" className="font-heading text-lg text-white hover:text-[#C9A96E] transition-colors block">Schneeblumen</a>
            <p className="text-xs text-[#7A9BB5] mt-1">{user?.full_name || 'Admin'}</p>
          </div>
          <nav className="py-3">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
                  className={`admin-sidebar-link w-full text-left ${activeTab === tab.id ? 'active' : ''}`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
          <div className="p-3 border-t border-[#354551]">
            <button
              onClick={() => { onLogout(); navigate('/'); }}
              className="admin-sidebar-link w-full text-left text-red-300 hover:text-red-200"
            >
              <LogOut size={18} />
              Abmelden
            </button>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main content */}
        <main className="flex-1 min-h-screen p-4 md:p-8" data-testid="admin-topbar">
          <div className="max-w-6xl mx-auto">
            {activeTab === 'overview' && <AdminOverview />}
            {activeTab === 'settings' && <AdminSettings />}
            {activeTab === 'cms' && <AdminCMS />}
            {activeTab === 'animals' && <AdminAnimals />}
            {activeTab === 'inquiries' && <AdminInquiries onSwitchTab={setActiveTab} />}
            {activeTab === 'media' && <AdminMedia />}
            {activeTab === 'reviews' && <AdminReviews />}
            {activeTab === 'affiliates' && <AdminAffiliates />}
            {activeTab === 'announcements' && <AdminAnnouncements />}
            {activeTab === 'litters' && <AdminLitters />}
            {activeTab === 'contracts' && <AdminContracts />}
            {activeTab === 'buyers' && <AdminBuyers />}
            {activeTab === 'preview' && <AdminDashboardPreview />}
          </div>
        </main>
      </div>
    </div>
  );
};

// ===== OVERVIEW =====
const AdminOverview = () => {
  const [stats, setStats] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const PREVIEW_URL = window.location.origin;

  useEffect(() => {
    Promise.all([
      getAnimals({ admin_view: true }),
      getInquiries(),
      getReviews(),
      getMedia()
    ]).then(([animals, inquiries, reviews, media]) => {
      setStats({
        animals: animals.data.length,
        inquiries: inquiries.data.filter(i => i.status === 'new').length,
        reviews: reviews.data.filter(r => r.status === 'pending').length,
        media: media.data.length
      });
    }).catch(console.error);
  }, []);

  return (
    <div>
      <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46] mb-6">Willkommen zurück!</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Tiere gesamt', value: stats.animals || 0, icon: Cat, color: '#7A9BB5' },
          { label: 'Neue Anfragen', value: stats.inquiries || 0, icon: MessageSquare, color: '#C9A96E' },
          { label: 'Offene Bewertungen', value: stats.reviews || 0, icon: Star, color: '#C9A96E' },
          { label: 'Medien', value: stats.media || 0, icon: Image, color: '#7A9BB5' },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="card-frost p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${stat.color}20` }}>
                  <Icon size={20} style={{ color: stat.color }} />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#2E3A46]">{stat.value}</p>
              <p className="text-xs text-[#7A9BB5] mt-1">{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* Site Preview */}
      <div className="card-frost overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#C5D0DA]/50 bg-[#EEF3F8]">
          <div className="flex items-center gap-2">
            <Monitor size={16} className="text-[#7A9BB5]" />
            <span className="text-sm font-medium text-[#2E3A46]">Live-Vorschau der Website</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowPreview(!showPreview)}
              className="text-xs px-3 py-1 rounded-lg bg-[#7A9BB5] text-white hover:bg-[#6889a3] transition-colors">
              {showPreview ? 'Ausblenden' : 'Anzeigen'}
            </button>
            <a href={PREVIEW_URL} target="_blank" rel="noopener noreferrer"
              className="text-xs px-3 py-1 rounded-lg bg-[#2E3A46] text-white hover:bg-[#354551] transition-colors flex items-center gap-1">
              <Eye size={12} /> In neuem Tab öffnen
            </a>
          </div>
        </div>
        {showPreview && (
          <div className="relative" style={{ height: '500px' }}>
            <iframe
              src={PREVIEW_URL}
              title="Website-Vorschau"
              className="w-full h-full border-0"
              style={{ pointerEvents: 'auto' }}
            />
          </div>
        )}
        {!showPreview && (
          <div className="py-12 text-center text-[#7A9BB5]">
            <Monitor size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Klicken Sie auf "Anzeigen" um die Live-Vorschau zu laden</p>
          </div>
        )}
      </div>

      {/* Dashboard Preview Link */}
      <div className="card-frost p-5 mt-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-[#2E3A46]">Nutzer-Dashboard</h3>
            <p className="text-sm text-[#7A9BB5]">Sehen Sie das Dashboard mit Wissensportal, Würfen, Verträgen und Empfehlungen.</p>
          </div>
          <a href="/dashboard" className="btn-primary flex items-center gap-2 shrink-0">
            <Eye size={16} /> Dashboard ansehen
          </a>
        </div>
      </div>
    </div>
  );
};

// ===== SETTINGS =====
const AdminSettings = () => {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings().then((res) => setSettings(res.data)).catch(console.error);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success('Einstellungen gespeichert!');
    } catch (err) { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const fontOptions = ['Playfair Display', 'DM Sans', 'Georgia', 'Lora', 'Merriweather', 'Roboto Slab', 'Source Serif Pro', 'Inter', 'Open Sans', 'Nunito', 'Poppins', 'Raleway'];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46]">Seiteneinstellungen</h1>
        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="settings-save-button">
          <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
        </button>
      </div>

      <div className="space-y-6">
        {/* Kitten Mode */}
        <div className="card-frost p-6">
          <h3 className="font-heading text-lg text-[#2E3A46] mb-4">Kitten-Modus & Ticker</h3>
          <label className="flex items-center gap-3 cursor-pointer mb-4" data-testid="settings-ticker-toggle">
            <div onClick={() => setSettings(s => ({ ...s, kitten_mode_active: s.kitten_mode_active === 'true' ? 'false' : 'true' }))}
              className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${settings.kitten_mode_active === 'true' ? 'bg-[#7A9BB5]' : 'bg-[#C5D0DA]'}`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${settings.kitten_mode_active === 'true' ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-[#2E3A46]">{settings.kitten_mode_active === 'true' ? 'Aktiv' : 'Inaktiv'} – Ticker & Kitten-Link</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Ticker-Text</label>
              <input type="text" value={settings.ticker_text || ''} onChange={(e) => setSettings(s => ({ ...s, ticker_text: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Ticker Hintergrundfarbe</label>
              <div className="flex gap-2 items-center">
                <input type="color" value={settings.ticker_bg_color || '#B91C1C'} onChange={(e) => setSettings(s => ({ ...s, ticker_bg_color: e.target.value }))}
                  className="w-10 h-10 rounded-lg border border-[#C5D0DA] cursor-pointer" />
                <input type="text" value={settings.ticker_bg_color || '#B91C1C'} onChange={(e) => setSettings(s => ({ ...s, ticker_bg_color: e.target.value }))}
                  className="flex-1 px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Ticker Textfarbe</label>
              <div className="flex gap-2 items-center">
                <input type="color" value={settings.ticker_text_color || '#FFFFFF'} onChange={(e) => setSettings(s => ({ ...s, ticker_text_color: e.target.value }))}
                  className="w-10 h-10 rounded-lg border border-[#C5D0DA] cursor-pointer" />
                <input type="text" value={settings.ticker_text_color || '#FFFFFF'} onChange={(e) => setSettings(s => ({ ...s, ticker_text_color: e.target.value }))}
                  className="flex-1 px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm" />
              </div>
            </div>
          </div>
          {/* Ticker Font, Size, Height */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Ticker Schriftart</label>
              <select value={settings.ticker_font || 'DM Sans'} onChange={(e) => setSettings(s => ({...s, ticker_font: e.target.value}))}
                className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm">
                {fontOptions.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Ticker Schriftgröße</label>
              <select value={settings.ticker_font_size || '14'} onChange={(e) => setSettings(s => ({...s, ticker_font_size: e.target.value}))}
                className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm">
                {['12','13','14','16','18','20','22','24'].map(s => <option key={s} value={s}>{s}px</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Ticker Balkenhöhe</label>
              <select value={settings.ticker_height || '40'} onChange={(e) => setSettings(s => ({...s, ticker_height: e.target.value}))}
                className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm">
                {['30','35','40','48','56','64'].map(s => <option key={s} value={s}>{s}px</option>)}
              </select>
            </div>
          </div>
          {/* Ticker Position */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-[#2E3A46] mb-2">Ticker-Position</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                {v:'above_banner',l:'Über dem Banner'},
                {v:'below_banner',l:'Unter dem Banner'},
                {v:'below_nav',l:'Unter der Menüleiste'},
              ].map(p => (
                <button key={p.v} type="button" onClick={() => setSettings(s => ({...s, ticker_position: p.v}))}
                  className={`px-3 py-2 rounded-lg text-xs font-medium text-left transition-all ${settings.ticker_position === p.v ? 'bg-[#2E3A46] text-white' : 'bg-[#EEF3F8] text-[#7A9BB5] border border-[#C5D0DA]'}`}>
                  {p.l}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-sm font-medium text-[#2E3A46] mb-2">Zusätzlich zwischen Abschnitten anzeigen</label>
            <div className="flex flex-wrap gap-2">
              {[
                {v:'queens',l:'Nach Katzen'},{v:'studs',l:'Nach Kater'},{v:'usps',l:'Nach USPs'},
                {v:'gallery',l:'Nach Galerie'},{v:'about',l:'Nach Über uns'}
              ].map(s => {
                const selected = (settings.ticker_between_sections || '').split(',').filter(Boolean);
                const isActive = selected.includes(s.v);
                return (
                  <button key={s.v} type="button" onClick={() => {
                    const current = (settings.ticker_between_sections || '').split(',').filter(Boolean);
                    const next = isActive ? current.filter(x => x !== s.v) : [...current, s.v];
                    setSettings(st => ({...st, ticker_between_sections: next.join(',')}));
                  }}
                    className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${isActive ? 'bg-[#7A9BB5] text-white' : 'bg-[#EEF3F8] text-[#7A9BB5] border border-[#C5D0DA]'}`}>
                    {s.l}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Ticker Preview */}
          {settings.kitten_mode_active === 'true' && (
            <div className="mt-4 rounded-xl overflow-hidden text-sm py-2 px-4 font-medium"
              style={{ backgroundColor: settings.ticker_bg_color || '#B91C1C', color: settings.ticker_text_color || '#FFFFFF' }}>
              Vorschau: {settings.ticker_text || 'Ticker-Text'}
            </div>
          )}
        </div>

        {/* Typography */}
        <div className="card-frost p-6">
          <h3 className="font-heading text-lg text-[#2E3A46] mb-4 flex items-center gap-2"><Type size={18} className="text-[#7A9BB5]" /> Schriftarten & Farben</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Überschriften-Schrift</label>
              <select value={settings.heading_font || 'Playfair Display'} onChange={(e) => setSettings(s => ({ ...s, heading_font: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none">
                {fontOptions.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <div className="flex gap-2 mt-2">
                <div><label className="block text-[10px] text-[#7A9BB5] mb-1">Größe</label>
                  <select value={settings.heading_size || '48'} onChange={(e) => setSettings(s => ({...s, heading_size: e.target.value}))}
                    className="px-2 py-1 rounded-lg border border-[#C5D0DA] text-xs">
                    {['32','36','40','44','48','52','56','60'].map(s => <option key={s} value={s}>{s}px</option>)}
                  </select></div>
                <div><label className="block text-[10px] text-[#7A9BB5] mb-1">Farbe</label>
                  <div className="flex gap-1 items-center">
                    <input type="color" value={settings.heading_color || '#2E3A46'} onChange={(e) => setSettings(s => ({...s, heading_color: e.target.value}))} className="w-7 h-7 rounded border border-[#C5D0DA] cursor-pointer" />
                    <input type="text" value={settings.heading_color || '#2E3A46'} onChange={(e) => setSettings(s => ({...s, heading_color: e.target.value}))} className="w-20 px-1 py-1 rounded border border-[#C5D0DA] text-[10px]" />
                  </div></div>
              </div>
              <p className="mt-2" style={{ fontFamily: settings.heading_font || 'Playfair Display', fontSize: `${(settings.heading_size || 48) / 3}px`, color: settings.heading_color || '#2E3A46' }}>Vorschau: Birma Schneeblumen</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Text-Schrift</label>
              <select value={settings.body_font || 'DM Sans'} onChange={(e) => setSettings(s => ({ ...s, body_font: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none">
                {fontOptions.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <div className="flex gap-2 mt-2">
                <div><label className="block text-[10px] text-[#7A9BB5] mb-1">Größe</label>
                  <select value={settings.body_size || '16'} onChange={(e) => setSettings(s => ({...s, body_size: e.target.value}))}
                    className="px-2 py-1 rounded-lg border border-[#C5D0DA] text-xs">
                    {['13','14','15','16','17','18','20'].map(s => <option key={s} value={s}>{s}px</option>)}
                  </select></div>
                <div><label className="block text-[10px] text-[#7A9BB5] mb-1">Farbe</label>
                  <div className="flex gap-1 items-center">
                    <input type="color" value={settings.body_color || '#354551'} onChange={(e) => setSettings(s => ({...s, body_color: e.target.value}))} className="w-7 h-7 rounded border border-[#C5D0DA] cursor-pointer" />
                    <input type="text" value={settings.body_color || '#354551'} onChange={(e) => setSettings(s => ({...s, body_color: e.target.value}))} className="w-20 px-1 py-1 rounded border border-[#C5D0DA] text-[10px]" />
                  </div></div>
              </div>
              <p className="mt-2" style={{ fontFamily: settings.body_font || 'DM Sans', fontSize: `${settings.body_size || 16}px`, color: settings.body_color || '#354551' }}>Vorschau: Herzlich willkommen bei den Schneeblumen.</p>
            </div>
          </div>
        </div>

        {/* Section Backgrounds */}
        <div className="card-frost p-6">
          <h3 className="font-heading text-lg text-[#2E3A46] mb-2 flex items-center gap-2"><Palette size={18} className="text-[#7A9BB5]" /> Bereiche – Hintergrund & Schrift</h3>
          <p className="text-xs text-[#7A9BB5] mb-4">Hintergrund (Farbe/Bild/Transparenz) + Schrift (Art/Größe/Farbe) pro Bereich.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { key: 'welcome', label: 'Willkommen' },
              { key: 'queens', label: 'Katzen' },
              { key: 'studs', label: 'Kater' },
              { key: 'usps', label: 'Warum Schneeblumen' },
              { key: 'gallery', label: 'Galerie' },
              { key: 'about', label: 'Über uns' },
              { key: 'contact', label: 'Kontakt' },
            ].map(({ key, label }) => {
              const colorKey = `section_bg_${key}`;
              const imgKey = `section_bg_image_${key}`;
              const opacityKey = `section_bg_opacity_${key}`;
              return (
                <div key={key} className="p-3 rounded-xl bg-[#EEF3F8] border border-[#C5D0DA]/50">
                  <label className="block text-xs font-medium text-[#2E3A46] mb-2">{label}</label>
                  <div className="flex gap-2 items-center mb-2">
                    <input type="color" value={settings[colorKey] || '#EEF3F8'} onChange={(e) => setSettings(s => ({ ...s, [colorKey]: e.target.value }))}
                      className="w-7 h-7 rounded border border-[#C5D0DA] cursor-pointer shrink-0" />
                    <input type="text" value={settings[colorKey] || ''} onChange={(e) => setSettings(s => ({ ...s, [colorKey]: e.target.value }))}
                      className="flex-1 px-2 py-1 rounded-lg border border-[#C5D0DA] bg-white text-[10px]" />
                  </div>
                  {/* BG Image */}
                  {settings[imgKey] && (
                    <div className="relative mb-1">
                      <img src={`${BACKEND_URL}${settings[imgKey]}`} alt="" className="w-full h-14 rounded-lg object-cover" />
                      <button onClick={() => { setSettings(s => { const n = {...s}; delete n[imgKey]; delete n[opacityKey]; return n; }); toast.success('Bild entfernt (Speichern nicht vergessen)'); }}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/80 text-white flex items-center justify-center hover:bg-red-600" title="Bild entfernen">
                        <X size={10} />
                      </button>
                    </div>
                  )}
                  <label className="text-[10px] text-[#7A9BB5] hover:text-[#2E3A46] cursor-pointer flex items-center gap-1">
                    <Upload size={10} /> {settings[imgKey] ? 'Bild ändern' : 'Hintergrundbild'}
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      if (e.target.files?.[0]) {
                        try {
                          const { uploadSectionBgImage } = await import('../api');
                          await uploadSectionBgImage(e.target.files[0], key);
                          toast.success('Hintergrundbild hochgeladen');
                          getSettings().then(r => setSettings(r.data));
                        } catch (err) { toast.error('Fehler'); }
                      }
                    }} />
                  </label>
                  {/* Opacity slider */}
                  {settings[imgKey] && (
                    <div className="mt-1">
                      <label className="text-[10px] text-[#7A9BB5]">Transparenz: {settings[opacityKey] || '30'}%</label>
                      <input type="range" min="0" max="100" value={settings[opacityKey] || '30'}
                        onChange={(e) => setSettings(s => ({ ...s, [opacityKey]: e.target.value }))}
                        className="w-full h-1 accent-[#7A9BB5]" />
                    </div>
                  )}
                  {/* Per-section typography */}
                  <div className="mt-2 pt-2 border-t border-[#C5D0DA]/30">
                    <p className="text-[9px] font-medium text-[#7A9BB5] mb-1">Schrift</p>
                    <div className="flex gap-1 mb-1">
                      <input type="color" value={settings[`section_heading_color_${key}`] || '#2E3A46'}
                        onChange={(e) => setSettings(s => ({...s, [`section_heading_color_${key}`]: e.target.value}))}
                        className="w-5 h-5 rounded border border-[#C5D0DA] cursor-pointer" title="Überschrift-Farbe" />
                      <input type="color" value={settings[`section_body_color_${key}`] || '#354551'}
                        onChange={(e) => setSettings(s => ({...s, [`section_body_color_${key}`]: e.target.value}))}
                        className="w-5 h-5 rounded border border-[#C5D0DA] cursor-pointer" title="Text-Farbe" />
                      <select value={settings[`section_heading_size_${key}`] || ''} onChange={(e) => setSettings(s => ({...s, [`section_heading_size_${key}`]: e.target.value}))}
                        className="px-1 py-0 rounded border border-[#C5D0DA] text-[9px] flex-1" title="Größe">
                        <option value="">Standard</option>
                        {['28','32','36','40','48','56'].map(v => <option key={v} value={v}>{v}px</option>)}
                      </select>
                    </div>
                    <div className="flex gap-1 text-[8px] text-[#C5D0DA]"><span>Überschrift</span><span>·</span><span>Text</span><span>·</span><span>Größe</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Banner-Verwaltung */}
        <div className="card-frost p-6">
          <h3 className="font-heading text-lg text-[#2E3A46] mb-4 flex items-center gap-2"><Image size={18} className="text-[#7A9BB5]" /> Banner-Bilder</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-2">Landingpage Banner</label>
              <div className="aspect-[3/1] rounded-xl overflow-hidden bg-[#D8E8F0] mb-2">
                {settings.banner_landing_url ? (
                  <img src={`${BACKEND_URL}${settings.banner_landing_url}`} alt="Landing Banner" className="w-full h-full object-cover" />
                ) : (
                  <img src={`${window.location.origin}/banner.jpg`} alt="Standard Banner" className="w-full h-full object-cover" />
                )}
              </div>
              <label className="btn-outline text-xs py-1.5 px-3 cursor-pointer inline-flex items-center gap-1">
                <Upload size={12} /> Banner ändern
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  if (e.target.files?.[0]) {
                    try {
                      const { uploadBanner } = await import('../api');
                      await uploadBanner(e.target.files[0], 'landing');
                      toast.success('Landingpage Banner hochgeladen');
                      getSettings().then(r => setSettings(r.data));
                    } catch (err) { toast.error('Fehler'); }
                  }
                }} />
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-2">Dashboard Banner</label>
              <div className="aspect-[3/1] rounded-xl overflow-hidden bg-[#D8E8F0] mb-2">
                {settings.banner_dashboard_url ? (
                  <img src={`${BACKEND_URL}${settings.banner_dashboard_url}`} alt="Dashboard Banner" className="w-full h-full object-cover" />
                ) : (
                  <img src={`${window.location.origin}/banner.jpg`} alt="Standard Banner" className="w-full h-full object-cover" />
                )}
              </div>
              <label className="btn-outline text-xs py-1.5 px-3 cursor-pointer inline-flex items-center gap-1">
                <Upload size={12} /> Banner ändern
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  if (e.target.files?.[0]) {
                    try {
                      const { uploadBanner } = await import('../api');
                      await uploadBanner(e.target.files[0], 'dashboard');
                      toast.success('Dashboard Banner hochgeladen');
                      getSettings().then(r => setSettings(r.data));
                    } catch (err) { toast.error('Fehler'); }
                  }
                }} />
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-2">Vertragsbanner (PDF)</label>
              <div className="aspect-[3/1] rounded-xl overflow-hidden bg-white border border-[#C5D0DA] mb-2">
                <img src={`/contract_banner.png?t=${Date.now()}`} alt="Vertragsbanner" className="w-full h-full object-contain" />
              </div>
              <label className="btn-outline text-xs py-1.5 px-3 cursor-pointer inline-flex items-center gap-1">
                <Upload size={12} /> Banner ändern
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  if (e.target.files?.[0]) {
                    try {
                      const formData = new FormData();
                      formData.append('file', e.target.files[0]);
                      const token = localStorage.getItem('auth_token');
                      await fetch(`${BACKEND_URL}/api/settings/contract-banner`, {
                        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
                      });
                      toast.success('Vertragsbanner aktualisiert');
                    } catch (err) { toast.error('Fehler'); }
                  }
                }} />
              </label>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="card-frost p-6">
          <h3 className="font-heading text-lg text-[#2E3A46] mb-4">Social Media</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Facebook URL</label>
              <input type="url" value={settings.facebook_url || ''} onChange={(e) => setSettings(s => ({ ...s, facebook_url: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Instagram URL</label>
              <input type="url" value={settings.instagram_url || ''} onChange={(e) => setSettings(s => ({ ...s, instagram_url: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none" />
            </div>
          </div>
        </div>

        {/* Navigation Styling */}
        <div className="card-frost p-6">
          <h3 className="font-heading text-lg text-[#2E3A46] mb-4">Menüleiste</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#2E3A46] mb-1">Hintergrundfarbe</label>
              <div className="flex gap-2 items-center">
                <input type="color" value={settings.nav_bg_color || '#2E3A46'} onChange={(e) => setSettings(s => ({...s, nav_bg_color: e.target.value}))}
                  className="w-8 h-8 rounded border border-[#C5D0DA] cursor-pointer" />
                <input type="text" value={settings.nav_bg_color || '#2E3A46'} onChange={(e) => setSettings(s => ({...s, nav_bg_color: e.target.value}))}
                  className="flex-1 px-2 py-1 rounded-lg border border-[#C5D0DA] text-xs" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#2E3A46] mb-1">Schriftfarbe</label>
              <div className="flex gap-2 items-center">
                <input type="color" value={settings.nav_text_color || '#C5D0DA'} onChange={(e) => setSettings(s => ({...s, nav_text_color: e.target.value}))}
                  className="w-8 h-8 rounded border border-[#C5D0DA] cursor-pointer" />
                <input type="text" value={settings.nav_text_color || '#C5D0DA'} onChange={(e) => setSettings(s => ({...s, nav_text_color: e.target.value}))}
                  className="flex-1 px-2 py-1 rounded-lg border border-[#C5D0DA] text-xs" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#2E3A46] mb-1">Schriftgröße</label>
              <select value={settings.nav_font_size || '18'} onChange={(e) => setSettings(s => ({...s, nav_font_size: e.target.value}))}
                className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm">
                {['14','16','18','20','22'].map(s => <option key={s} value={s}>{s}px</option>)}
              </select>
            </div>
          </div>

          {/* Nav Items Configuration */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-[#2E3A46] mb-2">Menüpunkte ein-/ausblenden & Reihenfolge</label>
            <p className="text-[10px] text-[#7A9BB5] mb-2">Klicken Sie auf einen Punkt um ihn ein-/auszublenden. Ziehen Sie die Reihenfolge mit den Pfeilen.</p>
            <div className="space-y-1">
              {(() => {
                const allItems = [
                  {key:'home',label:'Home'},{key:'about',label:'Über uns'},{key:'queens',label:'Katzen'},
                  {key:'studs',label:'Kater'},{key:'kitten',label:'Kitten'},{key:'ratgeber',label:'Ratgeber'},
                  {key:'gallery',label:'Galerie'},{key:'reviews',label:'Kundenstimmen'},{key:'contact',label:'Anfrage'}
                ];
                const hidden = (settings.nav_hidden_items || '').split(',').filter(Boolean);
                const orderStr = settings.nav_order || '';
                let ordered = [...allItems];
                if (orderStr) {
                  const keys = orderStr.split(',').filter(Boolean);
                  const sorted = [];
                  keys.forEach(k => { const item = ordered.find(i => i.key === k); if (item) sorted.push(item); });
                  ordered.forEach(item => { if (!sorted.find(s => s.key === item.key)) sorted.push(item); });
                  ordered = sorted;
                }
                const moveItem = (idx, dir) => {
                  const arr = [...ordered];
                  const newIdx = idx + dir;
                  if (newIdx < 0 || newIdx >= arr.length) return;
                  [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
                  setSettings(s => ({...s, nav_order: arr.map(i => i.key).join(',')}));
                };
                const toggleItem = (key) => {
                  const h = new Set(hidden);
                  if (h.has(key)) h.delete(key); else h.add(key);
                  setSettings(s => ({...s, nav_hidden_items: [...h].join(',')}));
                };
                return ordered.map((item, idx) => {
                  const isHidden = hidden.includes(item.key);
                  return (
                    <div key={item.key} className={`flex items-center gap-2 p-2 rounded-lg ${isHidden ? 'bg-red-50 opacity-50' : 'bg-[#EEF3F8]'}`}>
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => moveItem(idx, -1)} className="text-[#7A9BB5] hover:text-[#2E3A46] text-[10px] leading-none">▲</button>
                        <button onClick={() => moveItem(idx, 1)} className="text-[#7A9BB5] hover:text-[#2E3A46] text-[10px] leading-none">▼</button>
                      </div>
                      <span className="text-xs font-medium text-[#2E3A46] flex-1">{item.label}</span>
                      <button onClick={() => toggleItem(item.key)}
                        className={`text-[10px] px-2 py-0.5 rounded ${isHidden ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`}>
                        {isHidden ? 'Ausgeblendet' : 'Sichtbar'}
                      </button>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          <div className="mt-3 p-3 rounded-lg" style={{backgroundColor: settings.nav_bg_color || '#2E3A46'}}>
            <span style={{color: settings.nav_text_color || '#C5D0DA', fontSize: `${settings.nav_font_size || 18}px`}}>Vorschau: Home · Über uns · Katzen</span>
          </div>
        </div>

        {/* Google Reviews */}
        <div className="card-frost p-6">
          <h3 className="font-heading text-lg text-[#2E3A46] mb-4">Google Bewertungen</h3>
          <p className="text-xs text-[#7A9BB5] mb-4">Optional: Google Reviews automatisch auf der Landingpage anzeigen. Benötigt einen Google API Key + Place ID.</p>
          <div className="space-y-3">
            <div><label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Google Places API Key</label>
              <input type="password" value={settings.google_api_key || ''} onChange={(e) => setSettings(s => ({...s, google_api_key: e.target.value}))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none" placeholder="AIza..." /></div>
            <div><label className="block text-sm font-medium text-[#2E3A46] mb-1.5">Google Place ID</label>
              <input type="text" value={settings.google_place_id || ''} onChange={(e) => setSettings(s => ({...s, google_place_id: e.target.value}))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none" placeholder="ChIJ..." /></div>
          </div>

// ===== CMS =====
        </div>
      </div>
    </div>
  );
};
const AdminCMS = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAllCMS().then((res) => setItems(res.data)).catch(console.error);
  }, []);

  const handleSave = async (key, title, body) => {
    setSaving(true);
    try {
      await updateCMS(key, { title, body });
      toast.success('Inhalt gespeichert!');
      setEditing(null);
      getAllCMS().then((res) => setItems(res.data));
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const cmsLabels = {
    welcome_text: 'Willkommenstext',
    welcome_title: 'Willkommen Titel',
    usp_1: 'USP 1 – Aufgewachsen in der Familie',
    usp_2: 'USP 2 – Gesundheit vor Schema-F',
    usp_3: 'USP 3 – Chocolate & Lilac Spezialisten',
    usp_4: 'USP 4 – Persönliche Begleitung',
    about_text: 'Über uns',
    impressum_text: 'Impressum',
    datenschutz_text: 'Datenschutzerklärung',
    breeder_name: 'Züchtername',
    breeder_address: 'Adresse',
    breeder_phone: 'Telefonnummer',
    breeder_email: 'E-Mail Adresse',
    breeder_club: 'Vereinszugehörigkeit',
    breeder_zuchtbuch_nr: 'Zuchtbuchnummer',
    breeder_founded: 'Gründungsjahr',
    kitten_popup: 'Kitten-Popup Text',
  };

  const getLabel = (item) => {
    if (cmsLabels[item.key]) return cmsLabels[item.key];
    if (item.key?.startsWith('knowledge_')) return item.title || item.key;
    return item.title || item.key;
  };

  const isKnowledge = (key) => key?.startsWith('knowledge_');

  const handleCreateChapter = async () => {
    const title = prompt('Titel des neuen Wissenskapitels:');
    if (!title) return;
    try {
      await createKnowledgeChapter({ title, body: 'Inhalt folgt...' });
      toast.success('Kapitel erstellt');
      getAllCMS().then((res) => setItems(res.data));
    } catch (err) { toast.error('Fehler'); }
  };

  const handleDeleteChapter = async (key) => {
    if (!window.confirm('Wissenskapitel wirklich löschen?')) return;
    try {
      await deleteKnowledgeChapter(key);
      toast.success('Kapitel gelöscht');
      getAllCMS().then((res) => setItems(res.data));
    } catch (err) { toast.error('Fehler'); }
  };

  const handleImageUpload = async (key, e, position) => {
    const files = e.target.files;
    if (!files.length) return;
    try {
      for (let i = 0; i < files.length; i++) {
        await uploadKnowledgeImage(key, files[i], position || 'top');
      }
      toast.success('Bild(er) hinzugefügt');
      getAllCMS().then((res) => setItems(res.data));
    } catch (err) { toast.error('Fehler'); }
  };

  const generalItems = items.filter(i => !isKnowledge(i.key));
  const knowledgeItems = items.filter(i => isKnowledge(i.key));

  return (
    <div>
      <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46] mb-6">CMS-Inhalte</h1>

      {/* General CMS items */}
      <h2 className="font-heading text-lg text-[#2E3A46] mb-3">Allgemeine Inhalte</h2>
      <div className="space-y-3 mb-8">
        {generalItems.map((item) => (
          <div key={item.key} className="card-frost p-4">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-medium text-[#2E3A46] text-sm">{getLabel(item)}</h3>
              <button onClick={() => setEditing(editing === item.key ? null : item.key)} className="text-[#7A9BB5] hover:text-[#2E3A46] p-1"><Edit size={14} /></button>
            </div>
            {editing === item.key ? (
              <CMSEditor initialTitle={item.title} initialBody={item.body} onSave={(title, body) => handleSave(item.key, title, body)} onCancel={() => setEditing(null)} saving={saving} itemKey={item.key} images={item.image_url ? [item.image_url] : (item.images || [])} imagePosition={item.image_position} />
            ) : (
              <p className="text-xs text-[#7A9BB5] line-clamp-1">{item.body?.replace(/<[^>]*>/g, '').substring(0, 120)}...</p>
            )}
          </div>
        ))}
      </div>

      {/* Knowledge Portal items */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg text-[#2E3A46]">Wissensportal ({knowledgeItems.length} Kapitel)</h2>
        <button onClick={handleCreateChapter} className="btn-primary text-xs flex items-center gap-1"><Plus size={12} /> Neues Kapitel</button>
      </div>
      <div className="space-y-3">
        {knowledgeItems.map((item, idx) => (
          <div key={item.key} className="card-frost p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#C5D0DA] w-6">{String(idx + 1).padStart(2, '0')}</span>
                <h3 className="font-medium text-[#2E3A46] text-sm">{item.title || item.key}</h3>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(editing === item.key ? null : item.key)} className="text-[#7A9BB5] hover:text-[#2E3A46] p-1" title="Bearbeiten"><Edit size={14} /></button>
                <div className="relative group/upload">
                  <button className="text-[#7A9BB5] hover:text-[#2E3A46] p-1" title="Bild hinzufügen"><Upload size={14} /></button>
                  <div className="absolute right-0 top-full mt-1 bg-white border border-[#C5D0DA] rounded-xl shadow-lg z-10 hidden group-hover/upload:block w-44">
                    <p className="text-[10px] text-[#7A9BB5] px-3 pt-2 pb-1 font-medium">Bildposition wählen:</p>
                    {[{pos:'top',label:'Über dem Text'},{pos:'left',label:'Links vom Text'},{pos:'right',label:'Rechts vom Text'}].map(p => (
                      <label key={p.pos} className="flex items-center gap-2 px-3 py-2 text-xs text-[#2E3A46] hover:bg-[#EEF3F8] cursor-pointer">
                        <Upload size={12} className="text-[#7A9BB5]" /> {p.label}
                        <input type="file" accept="image/*" multiple onChange={(e) => handleImageUpload(item.key, e, p.pos)} className="hidden" />
                      </label>
                    ))}
                  </div>
                </div>
                <button onClick={() => handleDeleteChapter(item.key)} className="text-red-400 hover:text-red-600 p-1" title="Löschen"><Trash2 size={14} /></button>
              </div>
            </div>
            {item.images?.length > 0 && (
              <div className="flex items-center gap-2 mb-2">
                <div className="flex gap-1">{item.images.map((url, i) => <img key={i} src={BACKEND_URL + url} alt="" className="w-10 h-10 rounded object-cover" />)}</div>
                <span className="text-[10px] text-[#7A9BB5] bg-[#EEF3F8] px-2 py-0.5 rounded">
                  Position: {item.image_position === 'left' ? 'Links' : item.image_position === 'right' ? 'Rechts' : 'Oben'}
                </span>
              </div>
            )}
            {editing === item.key ? (
              <CMSEditor initialTitle={item.title} initialBody={item.body} onSave={(title, body) => handleSave(item.key, title, body)} onCancel={() => setEditing(null)} saving={saving} itemKey={item.key} images={item.images} imagePosition={item.image_position} />
            ) : (
              <p className="text-xs text-[#7A9BB5] line-clamp-1">{item.body?.replace(/<[^>]*>/g, '').substring(0, 120)}...</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const CMSEditor = ({ initialTitle, initialBody, onSave, onCancel, saving, itemKey, images, imagePosition }) => {
  const [title, setTitle] = useState(initialTitle || '');
  const [body, setBody] = useState(initialBody || '');
  const [imgPos, setImgPos] = useState(imagePosition || 'top');
  const [uploading, setUploading] = useState(false);
  const [currentImages, setCurrentImages] = useState(images || []);
  const isKnowledge = itemKey?.startsWith('knowledge_');
  const canHaveImages = isKnowledge || itemKey === 'about_text' || itemKey?.startsWith('usp_');

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length || !itemKey) return;
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        if (isKnowledge) {
          const result = await uploadKnowledgeImage(itemKey, files[i], imgPos);
          setCurrentImages(prev => [...prev, result.data.url]);
        } else {
          const { uploadCMSImage } = await import('../api');
          const result = await uploadCMSImage(itemKey, files[i]);
          setCurrentImages([result.data.url]);
        }
      }
      toast.success('Bild(er) hochgeladen');
    } catch (err) { toast.error('Fehler beim Upload'); }
    finally { setUploading(false); }
  };

  const handleDeleteImage = async (url) => {
    try {
      await deleteKnowledgeImage(itemKey, url);
      setCurrentImages(prev => prev.filter(u => u !== url));
      toast.success('Bild gelöscht');
    } catch (err) { toast.error('Fehler'); }
  };

  return (
    <div className="space-y-3 mt-3" data-testid="cms-editor">
      <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
        className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none text-sm" placeholder="Titel" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6}
        className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none text-sm" placeholder="Inhalt (HTML erlaubt)" />

      {/* Image Management */}
      {canHaveImages && (
        <div className="p-4 rounded-xl bg-[#EEF3F8] border border-[#C5D0DA]/50">
          <h5 className="text-sm font-medium text-[#2E3A46] mb-3">Bilder für dieses Kapitel</h5>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div>
              <label className="block text-xs text-[#7A9BB5] mb-1">Bildposition</label>
              <select value={imgPos} onChange={(e) => setImgPos(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-[#C5D0DA] bg-white text-sm">
                <option value="top">Über dem Text</option>
                <option value="left">Links vom Text</option>
                <option value="right">Rechts vom Text</option>
              </select>
            </div>
            <div className="pt-4">
              <label className="btn-primary text-xs py-1.5 px-3 cursor-pointer flex items-center gap-1">
                <Upload size={12} /> {uploading ? 'Hochladen...' : 'Bilder hochladen'}
                <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
              </label>
            </div>
          </div>
          {currentImages.length > 0 ? (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {currentImages.map((url, i) => (
                <div key={i} className="relative rounded-lg overflow-hidden aspect-square group">
                  <img src={`${BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => handleDeleteImage(url)}
                    className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={14} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[#C5D0DA] italic">Noch keine Bilder. Position wählen und dann hochladen.</p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={() => onSave(title, body)} disabled={saving} className="btn-primary text-sm flex items-center gap-1.5" data-testid="cms-save-button">
          <Save size={14} /> {saving ? 'Speichern...' : 'Speichern'}
        </button>
        <button onClick={onCancel} className="btn-outline text-sm">Abbrechen</button>
      </div>
    </div>
  );
};

// ===== ANIMALS =====
const AdminAnimals = () => {
  const [animals, setAnimals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editAnimal, setEditAnimal] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAnimals = () => {
    getAnimals({ admin_view: true }).then((res) => { setAnimals(res.data); setLoading(false); }).catch(console.error);
  };

  useEffect(() => { loadAnimals(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Tier wirklich löschen?')) return;
    try {
      await deleteAnimal(id);
      toast.success('Tier gelöscht');
      loadAnimals();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46]">Tiere verwalten</h1>
        <button
          onClick={() => { setEditAnimal(null); setShowForm(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="animal-create-button"
        >
          <Plus size={16} /> Neues Tier
        </button>
      </div>

      {showForm && (
        <AnimalForm
          animal={editAnimal}
          onClose={() => { setShowForm(false); setEditAnimal(null); }}
          onSaved={() => { setShowForm(false); setEditAnimal(null); loadAnimals(); }}
        />
      )}

      <div className="space-y-3">
        {animals.map((animal) => (
          <div key={animal.id} className="card-frost p-4 flex items-center gap-4" data-testid="admin-table">
            <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#D8E8F0] shrink-0">
              {animal.photos?.length > 0 ? (
                <img src={`${BACKEND_URL}${animal.photos[0]}`} alt={animal.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Cat size={24} className="text-[#C5D0DA]" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-[#2E3A46] truncate">{animal.name}</h3>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#D8E8F0] text-[#2E3A46]">
                  {animal.type === 'queen' ? 'Katze' : 'Kater'}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#7A9BB5]/15 text-[#7A9BB5]">
                  {animal.status}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#E8EEF3] text-[#354551]">
                  {animal.visibility}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => { setEditAnimal(animal); setShowForm(true); }}
                className="p-2 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5] transition-colors"
                data-testid="animal-edit-button"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={() => handleDelete(animal.id)}
                className="p-2 rounded-lg hover:bg-red-50 text-red-400 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {animals.length === 0 && !loading && (
          <div className="text-center py-12 text-[#7A9BB5]">
            <Cat size={48} className="mx-auto mb-3 opacity-50" />
            <p>Noch keine Tiere angelegt</p>
          </div>
        )}
      </div>
    </div>
  );
};

const AnimalForm = ({ animal, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: animal?.name || '', prefix: animal?.prefix || '', felidae_number: animal?.felidae_number || '',
    type: animal?.type || 'queen', status: animal?.status || 'breeding', visibility: animal?.visibility || 'landingpage',
    base_color: animal?.base_color || '', chocolate_carrier: animal?.chocolate_carrier || '', dilution_gene: animal?.dilution_gene || '', tabby_gene: animal?.tabby_gene || '',
    blood_group: animal?.blood_group || '', character_text: animal?.character_text || '', bio: animal?.bio || '', born_at: animal?.born_at || '',
    mother_id: animal?.mother_id || '', father_id: animal?.father_id || '',
    cattery_name: animal?.cattery_name || '', country_code: animal?.country_code || 'D',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [savedAnimal, setSavedAnimal] = useState(animal || null);
  const handleChange = (e) => { const { name, value } = e.target; setForm((prev) => ({ ...prev, [name]: value })); };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Name ist erforderlich'); return; }
    setSaving(true);
    try {
      let result;
      if (savedAnimal?.id) { result = await updateAnimal(savedAnimal.id, form); toast.success('Tier aktualisiert!'); }
      else { result = await createAnimal(form); toast.success('Tier angelegt! Sie können jetzt Fotos hochladen.'); }
      setSavedAnimal(result.data);
    } catch (err) { toast.error('Fehler'); } finally { setSaving(false); }
  };
  const handlePhotoUpload = async (e) => {
    if (!savedAnimal?.id) { toast.error('Bitte zuerst speichern'); return; }
    const files = e.target.files; if (!files.length) return;
    setUploading(true); setUploadProgress(0);
    try {
      for (let i = 0; i < files.length; i++) { setUploadProgress(Math.round(((i)/files.length)*100)); await uploadAnimalPhoto(savedAnimal.id, files[i]); }
      setUploadProgress(100); toast.success(files.length + ' Datei(en) hochgeladen');
      const updated = await getAnimal(savedAnimal.id); setSavedAnimal(updated.data);
    } catch (err) { toast.error('Fehler beim Upload'); } finally { setUploading(false); setUploadProgress(0); }
  };
  const handleSetMain = async (url) => {
    try { const { setAnimalMainPhoto } = await import('../api'); await setAnimalMainPhoto(savedAnimal.id, url); toast.success('Hauptbild gesetzt');
      const updated = await getAnimal(savedAnimal.id); setSavedAnimal(updated.data); } catch (err) { toast.error('Fehler'); }
  };
  const handlePhotoDelete = async (url) => {
    try { await deleteAnimalPhoto(savedAnimal.id, url); toast.success('Gelöscht');
      const updated = await getAnimal(savedAnimal.id); setSavedAnimal(updated.data); } catch (err) { toast.error('Fehler'); }
  };
  const photos = savedAnimal?.photos || [];
  return (
    <div className="card-frost p-6 mb-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl text-[#2E3A46]">{savedAnimal?.id ? 'Tier bearbeiten' : 'Neues Tier anlegen'}</h3>
        <button onClick={() => { onSaved(); onClose(); }} className="p-1.5 rounded-lg hover:bg-[#D8E8F0]"><X size={18} /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Name *</label><input name="name" value={form.name} onChange={handleChange} required className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" /></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Zuchtpräfix</label><input name="prefix" value={form.prefix} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="z.B. D*" /></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Felidae Nr.</label><input name="felidae_number" value={form.felidae_number} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" /></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Typ</label><select name="type" value={form.type} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none"><option value="queen">Zuchtkatze</option><option value="stud">Kater</option></select></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Status</label><select name="status" value={form.status} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none"><option value="kitten">Kitten</option><option value="youngster">Jungtier</option><option value="observing">In Beobachtung</option><option value="breeding">Zuchtkatze/Kater</option><option value="castrated">Kastrat</option><option value="retired">Ehemalige</option><option value="moved_out">Ausgezogen</option></select></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Sichtbarkeit</label><select name="visibility" value={form.visibility} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none"><option value="landingpage">Landingpage</option><option value="dashboard">Dashboard</option><option value="both">Beides</option><option value="hidden">Nicht sichtbar</option></select></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Farbe</label><input name="base_color" value={form.base_color} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="z.B. Chocolate Point" /></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Blutgruppe</label><select name="blood_group" value={form.blood_group} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none"><option value="">Unbekannt</option><option value="A">A</option><option value="B">B</option><option value="AB">AB</option></select></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Geburtsdatum</label><input name="born_at" type="date" value={form.born_at} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" /></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Verdünnung</label><select name="dilution_gene" value={form.dilution_gene} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none"><option value="">Keine Angabe</option><option value="trägt Verdünnung">Trägt Verdünnung</option><option value="zeigt Verdünnung">Zeigt Verdünnung</option></select></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Tabby Gen</label><select name="tabby_gene" value={form.tabby_gene} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none"><option value="">Keine Angabe</option><option value="ja">Ja</option><option value="nein">Nein</option><option value="Träger">Träger</option></select></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Chocolate-Träger</label><select name="chocolate_carrier" value={form.chocolate_carrier} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none"><option value="">Keine Angabe</option><option value="ja">Ja</option><option value="nein">Nein</option><option value="Träger">Träger</option></select></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Mutter (intern)</label><input name="mother_id" value={form.mother_id} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="Name oder ID" /></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Vater (intern)</label><input name="father_id" value={form.father_id} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="Name oder ID" /></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Zuchtname / Cattery</label><input name="cattery_name" value={form.cattery_name} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="z.B. von den Schneeblumen" /></div>
          <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Herkunftsland</label><select name="country_code" value={form.country_code} onChange={handleChange} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none">
            <option value="D">D – Deutschland</option><option value="A">A – Österreich</option><option value="CH">CH – Schweiz</option>
            <option value="F">F – Frankreich</option><option value="NL">NL – Niederlande</option><option value="DK">DK – Dänemark</option>
            <option value="S">S – Schweden</option><option value="GB">GB – Großbritannien</option><option value="US">US – USA</option>
            <option value="PL">PL – Polen</option><option value="CZ">CZ – Tschechien</option><option value="IT">IT – Italien</option>
          </select></div>
        </div>
        <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Wesenstext</label><textarea name="character_text" value={form.character_text} onChange={handleChange} rows={3} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="Persönliche Charakterbeschreibung..." /></div>
        <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Biographie</label><textarea name="bio" value={form.bio} onChange={handleChange} rows={3} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" placeholder="Ausführliche Beschreibung..." /></div>
        {/* Genetic Tests */}
        <div className="p-4 rounded-xl bg-[#EEF3F8] border border-[#C5D0DA]/50">
          <h4 className="text-sm font-medium text-[#2E3A46] mb-3">Gentests (Labogen/Laboklin)</h4>
          <div className="space-y-2">
            {(savedAnimal?.genetic_tests || []).map((test, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs p-2 bg-white rounded-lg">
                <span className="font-medium text-[#2E3A46]">{test.name}</span>
                <span className={`px-1.5 py-0.5 rounded ${test.result === 'negativ' ? 'bg-green-100 text-green-700' : test.result === 'positiv' ? 'bg-red-100 text-red-700' : 'bg-[#C9A96E]/15 text-[#C9A96E]'}`}>{test.result}</span>
                {test.lab && <span className="text-[#7A9BB5]">{test.lab}</span>}
                {test.date && <span className="text-[#C5D0DA]">{test.date}</span>}
              </div>
            ))}
          </div>
          {savedAnimal?.id && (
            <div className="flex flex-wrap gap-2 mt-2">
              <input id="gt-name" placeholder="Testname (z.B. PK-Def)" className="px-2 py-1 rounded-lg border border-[#C5D0DA] text-xs flex-1 min-w-[100px]" />
              <select id="gt-result" className="px-2 py-1 rounded-lg border border-[#C5D0DA] text-xs">
                <option value="negativ">Negativ</option><option value="positiv">Positiv</option><option value="Träger">Träger</option>
              </select>
              <input id="gt-lab" placeholder="Labor" className="px-2 py-1 rounded-lg border border-[#C5D0DA] text-xs w-24" />
              <input id="gt-date" type="date" className="px-2 py-1 rounded-lg border border-[#C5D0DA] text-xs" />
              <button type="button" onClick={async () => {
                const name = document.getElementById('gt-name').value;
                const result = document.getElementById('gt-result').value;
                const lab = document.getElementById('gt-lab').value;
                const date = document.getElementById('gt-date').value;
                if (!name) { toast.error('Testname erforderlich'); return; }
                const tests = [...(savedAnimal?.genetic_tests || []), {name, result, lab, date}];
                try { await updateAnimal(savedAnimal.id, {...form, genetic_tests: tests}); toast.success('Gentest hinzugefügt'); onSaved(); }
                catch(e) { toast.error('Fehler'); }
              }} className="btn-primary text-xs py-1 px-2"><Plus size={12} className="inline" /> Test</button>
            </div>
          )}
        </div>
        <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex items-center gap-2"><Save size={16} /> {saving ? 'Speichern...' : (savedAnimal?.id ? 'Aktualisieren' : 'Speichern & Fotos hochladen')}</button>
          <button type="button" onClick={() => { onSaved(); onClose(); }} className="btn-outline">Schließen</button></div>
      </form>
      {savedAnimal?.id && (
        <div className="mt-6 pt-6 border-t border-[#C5D0DA]/50">
          <div className="flex items-center justify-between mb-3"><h4 className="font-heading text-lg text-[#2E3A46]">Fotos & Videos</h4>
            <label className="btn-primary text-sm flex items-center gap-2 cursor-pointer"><Upload size={14} /> Dateien hochladen<input type="file" accept="image/*,video/*" multiple onChange={handlePhotoUpload} className="hidden" /></label></div>
          <p className="text-xs text-[#7A9BB5] mb-3">Das erste Bild wird als Vorschau verwendet. Klicken Sie auf den Stern um ein anderes Hauptbild zu wählen.</p>
          {uploading && (<div className="mb-4 p-3 rounded-xl bg-[#D8E8F0]"><div className="flex items-center justify-between mb-1"><span className="text-sm text-[#2E3A46]">Hochladen & Komprimieren...</span><span className="text-sm font-bold text-[#7A9BB5]">{uploadProgress}%</span></div><div className="w-full h-2 bg-[#C5D0DA] rounded-full overflow-hidden"><div className="h-full bg-[#7A9BB5] rounded-full transition-all" style={{width: uploadProgress+'%'}}></div></div></div>)}
          {photos.length > 0 ? (<div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">{photos.map((url, idx) => {
            const isVideo = url.match(/\.(mp4|mov|webm)$/i); const isMain = idx === 0;
            return (<div key={idx} className={"relative rounded-xl overflow-hidden aspect-square group " + (isMain ? "ring-2 ring-[#C9A96E]" : "")}>
              {isVideo ? <video src={BACKEND_URL+url} className="w-full h-full object-cover" muted /> : <img src={BACKEND_URL+url} alt="" className="w-full h-full object-cover" />}
              {isMain && <div className="absolute top-1 left-1 bg-[#C9A96E] text-white text-[10px] px-1.5 py-0.5 rounded font-medium">Hauptbild</div>}
              {isVideo && <div className="absolute top-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">Video</div>}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {!isMain && !isVideo && <button onClick={() => handleSetMain(url)} className="p-1.5 rounded-full bg-[#C9A96E]/90 text-white hover:bg-[#C9A96E]" title="Als Hauptbild"><Star size={12} /></button>}
                <button onClick={() => handlePhotoDelete(url)} className="p-1.5 rounded-full bg-red-500/80 text-white hover:bg-red-500" title="Löschen"><Trash2 size={12} /></button>
              </div></div>);
          })}</div>) : (<div className="text-center py-8 text-[#7A9BB5] border-2 border-dashed border-[#C5D0DA] rounded-xl"><Upload size={28} className="mx-auto mb-2 opacity-50" /><p className="text-sm">Noch keine Fotos. Laden Sie Bilder oder Videos hoch.</p></div>)}
        </div>)}
    </div>);
};


// ===== INQUIRIES =====
const AdminInquiries = ({ onSwitchTab }) => {
  const [inquiries, setInquiries] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  const loadInquiries = () => { getInquiries().then((res) => setInquiries(res.data)).catch(console.error); };
  useEffect(() => { loadInquiries(); }, []);

  const handleStatusChange = async (id, status) => {
    try {
      const res = await updateInquiry(id, { status });
      const data = res.data;
      if (data.buyer_created && ['interessent', 'kunde', 'kaeufer'].includes(status)) {
        const label = status === 'interessent' ? 'Interessent' : status === 'kunde' ? 'Kunde' : 'Käufer';
        toast.success(`Als ${label} eingestuft – Profil erstellt!`);
        if (window.confirm(`${label}-Profil wurde erstellt. Jetzt zum Bereich "Interessenten & Käufer" wechseln?`)) {
          if (onSwitchTab) onSwitchTab('buyers');
          return;
        }
      } else {
        toast.success('Kategorie aktualisiert');
      }
      loadInquiries();
    } catch (err) { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Anfrage wirklich löschen?')) return;
    try { await deleteInquiry(id); toast.success('Anfrage gelöscht'); loadInquiries(); }
    catch (err) { toast.error('Fehler'); }
  };

  const statusConfig = {
    new: { label: 'Neue Anfrage', color: 'bg-blue-100 text-blue-700', icon: '●' },
    allgemein: { label: 'Allgemeine Anfrage', color: 'bg-[#C5D0DA]/30 text-[#7A9BB5]', icon: '○' },
    interessent: { label: 'Interessent', color: 'bg-[#C9A96E]/15 text-[#C9A96E]', icon: '★' },
    kunde: { label: 'Kunde (aktiv)', color: 'bg-[#7A9BB5]/15 text-[#7A9BB5]', icon: '◆' },
    kaeufer: { label: 'Käufer', color: 'bg-green-100 text-green-700', icon: '✓' },
    closed: { label: 'Abgeschlossen', color: 'bg-[#C5D0DA]/20 text-[#C5D0DA]', icon: '✕' },
  };

  const filteredInquiries = filter === 'all' ? inquiries : inquiries.filter(i => i.status === filter);

  return (
    <div>
      <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46] mb-4">Anfragen & Zuordnung</h1>
      <p className="text-sm text-[#7A9BB5] mb-4">Ordnen Sie jede Anfrage einer Kategorie zu: Allgemein, Interessent, Kunde oder Käufer.</p>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[{v:'all',l:'Alle'},{v:'new',l:'Neu'},{v:'interessent',l:'Interessenten'},{v:'kunde',l:'Kunden'},{v:'kaeufer',l:'Käufer'},{v:'closed',l:'Abgeschlossen'}].map(f => (
          <button key={f.v} onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f.v ? 'bg-[#2E3A46] text-white' : 'bg-white text-[#7A9BB5] border border-[#C5D0DA]'}`}>
            {f.l} {f.v !== 'all' && <span className="ml-1 opacity-60">({inquiries.filter(i => f.v === 'all' || i.status === f.v).length})</span>}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filteredInquiries.map((inq) => {
          const sc = statusConfig[inq.status] || statusConfig.new;
          return (
            <div key={inq.id} className="card-frost p-4">
              <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(expandedId === inq.id ? null : inq.id)}>
                <div>
                  <h3 className="font-medium text-[#2E3A46]">{inq.name}</h3>
                  <p className="text-xs text-[#7A9BB5]">
                    <a href={`mailto:${inq.email}`} className="text-[#7A9BB5] hover:text-[#2E3A46] underline">{inq.email}</a>
                    {inq.phone && <> · <a href={`tel:${inq.phone}`} className="hover:text-[#2E3A46]">{inq.phone}</a></>}
                    {' · '}{new Date(inq.created_at).toLocaleDateString('de-DE')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.color}`}>{sc.label}</span>
                  <ChevronDown size={16} className={`text-[#7A9BB5] transition-transform ${expandedId === inq.id ? 'rotate-180' : ''}`} />
                </div>
              </div>
              {expandedId === inq.id && (
                <div className="mt-4 pt-4 border-t border-[#C5D0DA]/50 animate-fade-in">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-4">
                    <p><strong>E-Mail:</strong> <a href={`mailto:${inq.email}`} className="text-[#7A9BB5] hover:text-[#2E3A46] underline">{inq.email}</a></p>
                    <p><strong>Telefon:</strong> {inq.phone ? <a href={`tel:${inq.phone}`} className="text-[#7A9BB5] hover:text-[#2E3A46]">{inq.phone}</a> : '-'}</p>
                    <p><strong>PLZ:</strong> {inq.plz || '-'}</p>
                    <p><strong>Anfrage-Typ:</strong> {inq.inquiry_type === 'interest' ? 'Kitten-Interesse' : 'Allgemeine Frage'}</p>
                    {inq.housing && <p><strong>Wohnsituation:</strong> {inq.housing}</p>}
                    {inq.preferred_color && <p><strong>Farbwunsch:</strong> {inq.preferred_color}</p>}
                    {inq.preferred_gender && <p><strong>Geschlecht:</strong> {inq.preferred_gender}</p>}
                    {inq.wanted_timeframe && <p><strong>Zeitraum:</strong> {inq.wanted_timeframe}</p>}
                    {inq.how_found && <p><strong>Gefunden über:</strong> {inq.how_found}</p>}
                  </div>
                  {inq.message && <div className="p-3 bg-[#EEF3F8] rounded-lg text-sm mb-4"><strong>Nachricht:</strong> {inq.message}</div>}
                  {inq.intro_text && <div className="p-3 bg-[#EEF3F8] rounded-lg text-sm mb-4"><strong>Anschreiben:</strong> {inq.intro_text}</div>}

                  {/* Category Assignment */}
                  <div className="p-3 bg-[#D8E8F0] rounded-lg mb-3">
                    <p className="text-xs font-medium text-[#2E3A46] mb-2">Kategorie zuordnen:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(statusConfig).filter(([k]) => k !== 'new').map(([key, cfg]) => (
                        <button key={key} onClick={() => handleStatusChange(inq.id, key)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${inq.status === key ? cfg.color + ' ring-2 ring-offset-1 ring-[#7A9BB5]' : 'bg-white text-[#7A9BB5] border border-[#C5D0DA] hover:bg-[#EEF3F8]'}`}>
                          {cfg.icon} {cfg.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Link to buyer profile if exists */}
                  {inq.user_id && ['interessent','kunde','kaeufer'].includes(inq.status) && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-3">
                      <p className="text-xs text-green-700 font-medium">Nutzerprofil erstellt</p>
                      {inq.temp_password && <p className="text-xs text-green-600">Temporäres Passwort: <code className="bg-white px-1 rounded">{inq.temp_password}</code></p>}
                      <button onClick={() => onSwitchTab && onSwitchTab('buyers')} className="text-xs text-[#7A9BB5] hover:text-[#2E3A46] mt-1 underline">
                        → Zum Profil in "Interessenten & Käufer"
                      </button>
                    </div>
                  )}

                  <button onClick={() => handleDelete(inq.id)} className="text-xs py-1.5 px-3 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 flex items-center gap-1">
                    <Trash2 size={12} /> Anfrage löschen
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {filteredInquiries.length === 0 && (
          <div className="text-center py-12 text-[#7A9BB5]">
            <MessageSquare size={48} className="mx-auto mb-3 opacity-50" />
            <p>{filter === 'all' ? 'Noch keine Anfragen' : 'Keine Anfragen in dieser Kategorie'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== MEDIA =====
const AdminMedia = () => {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const loadMedia = () => { getMedia().then((res) => setMedia(res.data)).catch(console.error); };
  useEffect(() => { loadMedia(); }, []);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      for (let i = 0; i < files.length; i++) {
        const fileProgress = (p) => setUploadProgress(Math.round(((i * 100) + p) / files.length));
        await uploadMediaWithProgress(files[i], 'gallery', '', true, fileProgress);
      }
      toast.success(`${files.length} Datei(en) hochgeladen und komprimiert`);
      loadMedia();
    } catch (err) { toast.error('Fehler beim Upload'); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Medium wirklich löschen?')) return;
    try { await deleteMedia(id); toast.success('Gelöscht'); loadMedia(); }
    catch (err) { toast.error('Fehler'); }
  };

  const handleToggle = async (id) => {
    try { await toggleMediaVisibility(id); toast.success('Sichtbarkeit geändert'); loadMedia(); }
    catch (err) { toast.error('Fehler'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46]">Medien & Galerie</h1>
        <label className="btn-primary flex items-center gap-2 cursor-pointer">
          <Upload size={16} /> {uploading ? 'Hochladen...' : 'Hochladen'}
          <input type="file" accept="image/*,video/*" multiple onChange={handleUpload} className="hidden" data-testid="media-upload-input" />
        </label>
      </div>
      <p className="text-sm text-[#7A9BB5] mb-4">Bilder und Videos werden beim Hochladen automatisch komprimiert.</p>
      {uploading && (
        <div className="mb-4 p-3 rounded-xl bg-[#D8E8F0]">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-[#2E3A46]">Hochladen & Komprimieren...</span>
            <span className="text-sm font-bold text-[#7A9BB5]">{uploadProgress}%</span>
          </div>
          <div className="w-full h-2 bg-[#C5D0DA] rounded-full overflow-hidden">
            <div className="h-full bg-[#7A9BB5] rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="media-grid">
        {media.map((item) => (
          <div key={item.id} className={`relative group rounded-xl overflow-hidden aspect-square ${!item.is_public ? 'ring-2 ring-red-300 opacity-70' : ''}`}>
            {item.type === 'video' || item.url?.match(/\.(mp4|mov|webm|avi)$/i) ? (
              <video src={`${BACKEND_URL}${item.url}`} className="w-full h-full object-cover" muted />
            ) : (
              <img src={`${BACKEND_URL}${item.url}`} alt={item.caption || 'Medium'} className="w-full h-full object-cover" />
            )}
            {(item.type === 'video' || item.url?.match(/\.(mp4|mov|webm|avi)$/i)) && (
              <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">Video</div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button onClick={() => handleToggle(item.id)} className={`p-2 rounded-full text-white ${item.is_public ? 'bg-green-500/80 hover:bg-green-500' : 'bg-gray-500/80 hover:bg-gray-500'}`}
                title={item.is_public ? 'Sichtbar auf Landingpage' : 'Nicht sichtbar'}>
                {item.is_public ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              <button onClick={() => handleDelete(item.id)} className="p-2 rounded-full bg-red-500/80 text-white hover:bg-red-500"><Trash2 size={16} /></button>
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 flex items-center justify-between">
              <p className="text-white text-xs truncate">{item.filename || 'Bild'}</p>
              <span className={`text-xs px-1.5 py-0.5 rounded ${item.is_public ? 'bg-green-500/80 text-white' : 'bg-red-500/60 text-white'}`}>
                {item.is_public ? 'Sichtbar' : 'Versteckt'}
              </span>
            </div>
          </div>
        ))}
        {media.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#7A9BB5]">
            <Image size={48} className="mx-auto mb-3 opacity-50" /><p>Noch keine Medien hochgeladen</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== REVIEWS =====
const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', rating: 5, text: '' });
  const [newReview, setNewReview] = useState({ name: '', rating: 5, text: '', status: 'approved' });
  const [saving, setSaving] = useState(false);

  const loadReviews = () => { getReviews().then((res) => setReviews(res.data)).catch(console.error); };
  useEffect(() => { loadReviews(); }, []);

  const handleAction = async (id, status) => {
    try { await updateReview(id, { status }); toast.success(status === 'approved' ? 'Freigegeben' : 'Abgelehnt'); loadReviews(); }
    catch (err) { toast.error('Fehler'); }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm('Bewertung löschen?')) return;
    try { await deleteReview(id); toast.success('Gelöscht'); loadReviews(); }
    catch (err) { toast.error('Fehler'); }
  };

  const handleCreateReview = async () => {
    if (!newReview.name || !newReview.text) { toast.error('Name und Text erforderlich'); return; }
    setSaving(true);
    try { await createReviewAdmin(newReview); toast.success('Bewertung erstellt'); setShowCreate(false); setNewReview({ name: '', rating: 5, text: '', status: 'approved' }); loadReviews(); }
    catch (err) { toast.error('Fehler'); }
    finally { setSaving(false); }
  };

  const handleEdit = async (id) => {
    setSaving(true);
    try { await updateReview(id, editForm); toast.success('Bewertung aktualisiert'); setEditingId(null); loadReviews(); }
    catch (err) { toast.error('Fehler'); }
    finally { setSaving(false); }
  };

  const handlePhotoUpload = async (reviewId, e) => {
    const files = e.target.files;
    if (!files.length) return;
    try {
      for (let i = 0; i < files.length; i++) { await uploadReviewPhoto(reviewId, files[i]); }
      toast.success('Foto(s) hochgeladen');
      loadReviews();
    } catch (err) { toast.error('Fehler beim Upload'); }
  };

  const statusColors = { pending: 'bg-[#C9A96E]/15 text-[#C9A96E]', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46]">Bewertungen</h1>
        <button onClick={() => setShowCreate(!showCreate)} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Bewertung erstellen
        </button>
      </div>

      {showCreate && (
        <div className="card-frost p-6 mb-6 animate-fade-in">
          <h3 className="font-heading text-lg text-[#2E3A46] mb-4">Neue Bewertung manuell erstellen</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Name *</label>
                <input value={newReview.name} onChange={(e) => setNewReview(r => ({ ...r, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" /></div>
              <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Sterne</label>
                <div className="flex gap-1 pt-1">
                  {[1, 2, 3, 4, 5].map(s => (
                    <button key={s} type="button" onClick={() => setNewReview(r => ({ ...r, rating: s }))} className="p-0.5">
                      <Star size={20} className={s <= newReview.rating ? 'text-[#C9A96E] fill-[#C9A96E]' : 'text-[#C5D0DA]'} />
                    </button>
                  ))}
                </div></div>
            </div>
            <div><label className="block text-sm font-medium text-[#2E3A46] mb-1">Text *</label>
              <textarea value={newReview.text} onChange={(e) => setNewReview(r => ({ ...r, text: e.target.value }))}
                rows={3} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" /></div>
            <div className="flex gap-3">
              <button onClick={handleCreateReview} disabled={saving} className="btn-primary text-sm flex items-center gap-1.5"><Save size={14} /> {saving ? 'Speichern...' : 'Erstellen'}</button>
              <button onClick={() => setShowCreate(false)} className="btn-outline text-sm">Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {reviews.map((review) => (
          <div key={review.id} className="card-frost p-5">
            {editingId === review.id ? (
              <div className="space-y-3 animate-fade-in">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><label className="block text-xs text-[#7A9BB5] mb-1">Name</label>
                    <input value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-[#C5D0DA] text-sm" /></div>
                  <div><label className="block text-xs text-[#7A9BB5] mb-1">Sterne</label>
                    <div className="flex gap-1 pt-1">{[1,2,3,4,5].map(s => (
                      <button key={s} type="button" onClick={() => setEditForm(f => ({ ...f, rating: s }))} className="p-0.5">
                        <Star size={18} className={s <= editForm.rating ? 'text-[#C9A96E] fill-[#C9A96E]' : 'text-[#C5D0DA]'} />
                      </button>
                    ))}</div></div>
                </div>
                <div><label className="block text-xs text-[#7A9BB5] mb-1">Text</label>
                  <textarea value={editForm.text} onChange={(e) => setEditForm(f => ({ ...f, text: e.target.value }))}
                    rows={3} className="w-full px-3 py-2 rounded-lg border border-[#C5D0DA] text-sm" /></div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(review.id)} disabled={saving} className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1"><Save size={12} /> Speichern</button>
                  <button onClick={() => setEditingId(null)} className="btn-outline text-xs py-1.5 px-3">Abbrechen</button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-[#2E3A46]">{review.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[review.status] || statusColors.pending}`}>
                        {review.status === 'pending' ? 'Ausstehend' : review.status === 'approved' ? 'Freigegeben' : 'Abgelehnt'}
                      </span>
                    </div>
                    <div className="flex gap-0.5 mb-2">{[1,2,3,4,5].map((s) => (
                      <Star key={s} size={14} className={s <= review.rating ? 'text-[#C9A96E] fill-[#C9A96E]' : 'text-[#C5D0DA]'} />
                    ))}</div>
                    <p className="text-sm text-[#354551]">{review.text}</p>
                    {/* Photos */}
                    {review.photos?.length > 0 && (
                      <div className="flex gap-2 mt-3">{review.photos.map((url, i) => (
                        <img key={i} src={`${BACKEND_URL}${url}`} alt="" className="w-16 h-16 rounded-lg object-cover" />
                      ))}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0 ml-2">
                    <button onClick={() => { setEditingId(review.id); setEditForm({ name: review.name, rating: review.rating, text: review.text }); }}
                      className="p-1.5 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5]" title="Bearbeiten"><Edit size={14} /></button>
                    {review.status !== 'approved' && (
                      <button onClick={() => handleAction(review.id, 'approved')} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600" title="Freigeben"><Check size={14} /></button>
                    )}
                    {review.status !== 'rejected' && (
                      <button onClick={() => handleAction(review.id, 'rejected')} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Ablehnen"><X size={14} /></button>
                    )}
                    <label className="p-1.5 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5] cursor-pointer" title="Foto hinzufügen">
                      <Upload size={14} />
                      <input type="file" accept="image/*" multiple onChange={(e) => handlePhotoUpload(review.id, e)} className="hidden" />
                    </label>
                    <button onClick={() => handleDeleteReview(review.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Löschen"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {reviews.length === 0 && (
          <div className="text-center py-12 text-[#7A9BB5]">
            <Star size={48} className="mx-auto mb-3 opacity-50" /><p>Noch keine Bewertungen</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== AFFILIATES =====
const AdminAffiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editAff, setEditAff] = useState(null);

  const loadAffiliates = () => {
    getAffiliates().then((res) => setAffiliates(res.data)).catch(console.error);
  };

  useEffect(() => { loadAffiliates(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Affiliate-Link löschen?')) return;
    try {
      await deleteAffiliate(id);
      toast.success('Affiliate gelöscht');
      loadAffiliates();
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const categories = [
    'wasserfilter', 'nassfutter', 'trockenfutter', 'kratzbaum', 'spielzeug',
    'sicherheitsnetz', 'pflegeprodukt', 'transportbox', 'streu'
  ];

  const catLabels = {
    wasserfilter: 'Wasserfilter', nassfutter: 'Nassfutter', trockenfutter: 'Trockenfutter',
    kratzbaum: 'Kratzbäume', spielzeug: 'Spielzeug', sicherheitsnetz: 'Sicherheitsnetze',
    pflegeprodukt: 'Pflegeprodukte', transportbox: 'Transportboxen', streu: 'Streu'
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46]">Affiliate-Links</h1>
        <button onClick={() => { setEditAff(null); setShowForm(true); }} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Neuer Link
        </button>
      </div>

      {showForm && (
        <AffiliateForm
          affiliate={editAff}
          categories={categories}
          catLabels={catLabels}
          onClose={() => { setShowForm(false); setEditAff(null); }}
          onSaved={() => { setShowForm(false); setEditAff(null); loadAffiliates(); }}
        />
      )}

      <div className="space-y-3">
        {affiliates.map((aff) => (
          <div key={aff.id} className="card-frost p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-[#2E3A46] truncate">{aff.name}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#D8E8F0] text-[#2E3A46]">
                {catLabels[aff.category] || aff.category}
              </span>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => { setEditAff(aff); setShowForm(true); }} className="p-2 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5]">
                <Edit size={16} />
              </button>
              <button onClick={() => handleDelete(aff.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-400">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {affiliates.length === 0 && (
          <div className="text-center py-12 text-[#7A9BB5]">
            <Link2 size={48} className="mx-auto mb-3 opacity-50" />
            <p>Noch keine Affiliate-Links</p>
          </div>
        )}
      </div>
    </div>
  );
};

const AffiliateForm = ({ affiliate, categories, catLabels, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: affiliate?.name || '',
    description: affiliate?.description || '',
    category: affiliate?.category || categories[0],
    image_url: affiliate?.image_url || '',
    affiliate_url: affiliate?.affiliate_url || '',
    is_active: affiliate?.is_active !== false,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (affiliate?.id) {
        await updateAffiliate(affiliate.id, form);
      } else {
        await createAffiliate(form);
      }
      toast.success('Affiliate gespeichert!');
      onSaved();
    } catch (err) {
      toast.error('Fehler');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-frost p-6 mb-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg text-[#2E3A46]">{affiliate?.id ? 'Bearbeiten' : 'Neuer Affiliate-Link'}</h3>
        <button onClick={onClose} className="p-1"><X size={18} /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-[#2E3A46] mb-1">Name</label>
            <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required
              className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2E3A46] mb-1">Kategorie</label>
            <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none">
              {categories.map((c) => <option key={c} value={c}>{catLabels[c]}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2E3A46] mb-1">Affiliate URL</label>
          <input type="url" value={form.affiliate_url} onChange={(e) => setForm(f => ({ ...f, affiliate_url: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2E3A46] mb-1">Beschreibung</label>
          <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            rows={2} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" />
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="btn-primary text-sm flex items-center gap-1.5">
            <Save size={14} /> {saving ? 'Speichern...' : 'Speichern'}
          </button>
          <button type="button" onClick={onClose} className="btn-outline text-sm">Abbrechen</button>
        </div>
      </form>
    </div>
  );
};

// ===== LITTERS =====
const AdminLitters = () => {
  const [litters, setLitters] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editLitter, setEditLitter] = useState(null);

  const loadData = () => {
    Promise.all([getLitters(), getAnimals({ admin_view: true })]).then(([l, a]) => {
      setLitters(l.data);
      setAnimals(a.data);
    }).catch(console.error);
  };

  useEffect(() => { loadData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Wurf wirklich löschen?')) return;
    try {
      await deleteLitter(id);
      toast.success('Wurf gelöscht');
      loadData();
    } catch (err) { toast.error('Fehler'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46]">Würfe verwalten</h1>
        <button onClick={() => { setEditLitter(null); setShowForm(true); }} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Neuer Wurf
        </button>
      </div>

      {showForm && (
        <LitterForm
          litter={editLitter}
          animals={animals}
          onClose={() => { setShowForm(false); setEditLitter(null); }}
          onSaved={() => { setShowForm(false); setEditLitter(null); loadData(); }}
        />
      )}

      <div className="space-y-3">
        {litters.map((litter) => (
          <div key={litter.id} className="card-frost p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-[#2E3A46]">{litter.name}</h3>
                <div className="flex flex-wrap gap-2 mt-1 text-xs text-[#7A9BB5]">
                  {litter.mother && <span>Mutter: {litter.mother.name}</span>}
                  {litter.father && <span>· Vater: {litter.father.name}</span>}
                  {litter.born_at && <span>· Geboren: {litter.born_at}</span>}
                  <span>· Kitten: {litter.kittens?.length || 0}</span>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditLitter(litter); setShowForm(true); }} className="p-2 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5]">
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(litter.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {litters.length === 0 && (
          <div className="text-center py-12 text-[#7A9BB5]">
            <Baby size={48} className="mx-auto mb-3 opacity-50" />
            <p>Noch keine Würfe angelegt</p>
          </div>
        )}
      </div>
    </div>
  );
};

const LitterForm = ({ litter, animals, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: litter?.name || '', mother_id: litter?.mother_id || '', father_id: litter?.father_id || '',
    born_at: litter?.born_at || '', expected_birth_at: litter?.expected_birth_at || '',
    notes: litter?.notes || '', is_published: litter?.is_published || false, is_active: litter?.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [savedLitter, setSavedLitter] = useState(litter || null);
  const [kittenForm, setKittenForm] = useState({ name: '', gender: '', color: '', character_text: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const queens = animals.filter(a => a.type === 'queen');
  const studs = animals.filter(a => a.type === 'stud');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let result;
      if (savedLitter?.id) { result = await updateLitter(savedLitter.id, form); toast.success('Wurf aktualisiert!'); }
      else { result = await createLitter(form); toast.success('Wurf angelegt!'); }
      setSavedLitter(result.data);
      if (savedLitter?.id) onSaved();
    } catch (err) { toast.error('Fehler'); }
    finally { setSaving(false); }
  };

  const handleAddKitten = async () => {
    if (!savedLitter?.id) { toast.error('Bitte zuerst speichern'); return; }
    try {
      await createKitten({ ...kittenForm, litter_id: savedLitter.id });
      toast.success('Kitten hinzugefügt');
      setKittenForm({ name: '', gender: '', color: '', character_text: '' });
      onSaved();
    } catch (err) { toast.error('Fehler'); }
  };

  const handlePhotoUpload = async (e) => {
    if (!savedLitter?.id) { toast.error('Bitte zuerst speichern'); return; }
    const files = e.target.files; if (!files.length) return;
    setUploading(true); setUploadProgress(0);
    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress(Math.round((i / files.length) * 100));
        await uploadLitterPhoto(savedLitter.id, files[i]);
      }
      setUploadProgress(100);
      toast.success(`${files.length} Datei(en) hochgeladen`);
      onSaved();
    } catch (err) { toast.error('Fehler beim Upload'); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const handleDeletePhoto = async (url) => {
    try { await deleteLitterPhoto(savedLitter.id, url); toast.success('Gelöscht'); onSaved(); }
    catch (err) { toast.error('Fehler'); }
  };

  const photos = savedLitter?.photos || litter?.photos || [];

  return (
    <div className="card-frost p-6 mb-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl text-[#2E3A46]">{litter?.id ? 'Wurf bearbeiten' : 'Neuer Wurf'}</h3>
        <button onClick={onClose} className="p-1.5"><X size={18} /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#2E3A46] mb-1">Wurfname *</label>
            <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required
              className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none"
              placeholder="z.B. F-Wurf" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2E3A46] mb-1">Mutter</label>
            <select value={form.mother_id} onChange={(e) => setForm(f => ({ ...f, mother_id: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none">
              <option value="">Auswählen...</option>
              {queens.map(a => <option key={a.id} value={a.id}>{a.name} ({a.base_color})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2E3A46] mb-1">Vater</label>
            <select value={form.father_id} onChange={(e) => setForm(f => ({ ...f, father_id: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none">
              <option value="">Auswählen...</option>
              {studs.map(a => <option key={a.id} value={a.id}>{a.name} ({a.base_color})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2E3A46] mb-1">Geburtsdatum</label>
            <input type="date" value={form.born_at} onChange={(e) => setForm(f => ({ ...f, born_at: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2E3A46] mb-1">Erwarteter Termin</label>
            <input type="date" value={form.expected_birth_at} onChange={(e) => setForm(f => ({ ...f, expected_birth_at: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" />
          </div>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_published} onChange={(e) => setForm(f => ({ ...f, is_published: e.target.checked }))}
                className="w-4 h-4 accent-[#7A9BB5]" />
              Veröffentlicht
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))}
                className="w-4 h-4 accent-[#7A9BB5]" />
              Aktiv
            </label>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2E3A46] mb-1">Notizen</label>
          <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={2} className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none" />
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="btn-primary text-sm flex items-center gap-1.5">
            <Save size={14} /> {saving ? 'Speichern...' : (savedLitter?.id ? 'Aktualisieren' : 'Speichern & Medien hochladen')}
          </button>
          <button type="button" onClick={() => { onSaved(); onClose(); }} className="btn-outline text-sm">Schließen</button>
        </div>
      </form>

      {/* Photos & Videos for litter */}
      {savedLitter?.id && (
        <div className="mt-6 pt-6 border-t border-[#C5D0DA]/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-heading text-base text-[#2E3A46]">Fotos & Videos</h4>
            <label className="btn-primary text-xs py-1.5 px-3 cursor-pointer flex items-center gap-1">
              <Upload size={12} /> Hochladen
              <input type="file" accept="image/*,video/*" multiple onChange={handlePhotoUpload} className="hidden" />
            </label>
          </div>
          {uploading && (
            <div className="mb-3 p-2 rounded-lg bg-[#D8E8F0]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[#2E3A46]">Hochladen...</span>
                <span className="text-xs font-bold text-[#7A9BB5]">{uploadProgress}%</span>
              </div>
              <div className="w-full h-1.5 bg-[#C5D0DA] rounded-full overflow-hidden">
                <div className="h-full bg-[#7A9BB5] rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            </div>
          )}
          {photos.length > 0 ? (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
              {photos.map((url, idx) => {
                const isVideo = url.match(/\.(mp4|mov|webm)$/i);
                return (
                  <div key={idx} className="relative rounded-lg overflow-hidden aspect-square group">
                    {isVideo ? <video src={`${BACKEND_URL}${url}`} className="w-full h-full object-cover" muted />
                      : <img src={`${BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />}
                    {isVideo && <div className="absolute top-1 right-1 bg-black/60 text-white text-[9px] px-1 rounded">Video</div>}
                    <button onClick={() => handleDeletePhoto(url)}
                      className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 size={14} className="text-white" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-[#C5D0DA] italic">Noch keine Fotos für diesen Wurf.</p>
          )}
        </div>
      )}

      {/* Add kittens to litter */}
      {savedLitter?.id && (
        <div className="mt-6 pt-6 border-t border-[#C5D0DA]/50">
          <h4 className="font-medium text-[#2E3A46] mb-3">Kitten hinzufügen</h4>
          <div className="flex flex-wrap gap-2 mb-3">
            <input value={kittenForm.name} onChange={(e) => setKittenForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Name" className="px-3 py-1.5 rounded-lg border border-[#C5D0DA] text-sm w-32" />
            <select value={kittenForm.gender} onChange={(e) => setKittenForm(f => ({ ...f, gender: e.target.value }))}
              className="px-3 py-1.5 rounded-lg border border-[#C5D0DA] text-sm">
              <option value="">Geschlecht</option>
              <option value="male">Männlich</option>
              <option value="female">Weiblich</option>
            </select>
            <input value={kittenForm.color} onChange={(e) => setKittenForm(f => ({ ...f, color: e.target.value }))}
              placeholder="Farbe" className="px-3 py-1.5 rounded-lg border border-[#C5D0DA] text-sm w-36" />
            <button onClick={handleAddKitten} className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1">
              <Plus size={12} /> Kitten
            </button>
          </div>
          {litter.kittens?.length > 0 && (
            <div className="space-y-2">
              {litter.kittens.map(k => (
                <KittenRow key={k.id} kitten={k} onSaved={onSaved} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const KittenRow = ({ kitten, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: kitten.name || '', gender: kitten.gender || '', color: kitten.color || '', character_text: kitten.character_text || '', status: kitten.status || 'available', litter_id: kitten.litter_id });
  const handleSave = async () => {
    try { await updateKitten(kitten.id, form); toast.success('Kitten aktualisiert'); setEditing(false); onSaved(); }
    catch (err) { toast.error('Fehler'); }
  };
  const handlePhoto = async (e) => {
    if (!e.target.files?.length) return;
    try { await uploadKittenPhoto(kitten.id, e.target.files[0]); toast.success('Foto hochgeladen'); onSaved(); }
    catch (err) { toast.error('Fehler'); }
  };
  return (
    <div className="p-3 rounded-xl bg-[#EEF3F8] border border-[#C5D0DA]/50">
      {editing ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} placeholder="Name" className="px-2 py-1 rounded-lg border border-[#C5D0DA] text-sm flex-1 min-w-[120px]" />
            <select value={form.gender} onChange={(e) => setForm(f => ({...f, gender: e.target.value}))} className="px-2 py-1 rounded-lg border border-[#C5D0DA] text-sm">
              <option value="">Geschlecht</option><option value="male">Männlich</option><option value="female">Weiblich</option>
            </select>
            <input value={form.color} onChange={(e) => setForm(f => ({...f, color: e.target.value}))} placeholder="Farbe" className="px-2 py-1 rounded-lg border border-[#C5D0DA] text-sm flex-1 min-w-[100px]" />
            <select value={form.status} onChange={(e) => setForm(f => ({...f, status: e.target.value}))} className="px-2 py-1 rounded-lg border border-[#C5D0DA] text-sm">
              <option value="available">Verfügbar</option><option value="reserved">Reserviert</option><option value="sold">Vergeben</option>
            </select>
          </div>
          <textarea value={form.character_text} onChange={(e) => setForm(f => ({...f, character_text: e.target.value}))} placeholder="Wesenstext" rows={2} className="w-full px-2 py-1 rounded-lg border border-[#C5D0DA] text-sm" />
          <div className="flex gap-2">
            <button onClick={handleSave} className="btn-primary text-xs py-1 px-3"><Save size={12} className="inline mr-1" />Speichern</button>
            <button onClick={() => setEditing(false)} className="btn-outline text-xs py-1 px-3">Abbrechen</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {kitten.photos?.length > 0 && <img src={`${BACKEND_URL}${kitten.photos[0]}`} alt="" className="w-8 h-8 rounded object-cover" />}
            <div>
              <span className="text-sm font-medium text-[#2E3A46]">{kitten.name || 'Unbenannt'}</span>
              <span className="text-xs text-[#7A9BB5] ml-2">{kitten.color} · {kitten.gender === 'male' ? 'M' : kitten.gender === 'female' ? 'W' : '?'}</span>
              <span className={`text-xs ml-2 px-1.5 py-0.5 rounded ${kitten.status === 'available' ? 'bg-green-100 text-green-700' : kitten.status === 'reserved' ? 'bg-[#C9A96E]/15 text-[#C9A96E]' : 'bg-[#C5D0DA]/30 text-[#7A9BB5]'}`}>
                {kitten.status === 'available' ? 'Verfügbar' : kitten.status === 'reserved' ? 'Reserviert' : 'Vergeben'}
              </span>
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-white text-[#7A9BB5]"><Edit size={13} /></button>
            <label className="p-1 rounded hover:bg-white text-[#7A9BB5] cursor-pointer"><Upload size={13} /><input type="file" accept="image/*" onChange={handlePhoto} className="hidden" /></label>
            <button onClick={async () => { await deleteKitten(kitten.id); toast.success('Entfernt'); onSaved(); }} className="p-1 rounded hover:bg-red-50 text-red-400"><Trash2 size={13} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== BUYERS =====
const AdminBuyers = () => {
  const [buyers, setBuyers] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', full_name: '', phone: '', buyer_type: 'interessent' });
  const [createdInfo, setCreatedInfo] = useState(null);
  const [savingCreate, setSavingCreate] = useState(false);

  const loadData = () => { getBuyers().then(b => setBuyers(b.data)).catch(console.error); };
  useEffect(() => { loadData(); }, []);

  const handleSaveBuyer = async (userId, data) => {
    try { await updateBuyerProfile(userId, data); toast.success('Profil aktualisiert'); loadData(); }
    catch (err) { toast.error('Fehler'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Nutzer wirklich löschen?')) return;
    try { await deleteUser(userId); toast.success('Gelöscht'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const handleCreate = async () => {
    if (!createForm.email || !createForm.full_name) { toast.error('E-Mail und Name erforderlich'); return; }
    setSavingCreate(true);
    try {
      const res = await adminCreateUser(createForm);
      setCreatedInfo(res.data);
      toast.success('Kontakt erstellt!');
      setCreateForm({ email: '', full_name: '', phone: '', buyer_type: 'interessent' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
    finally { setSavingCreate(false); }
  };

  const handleResetPw = async (userId, name) => {
    if (!window.confirm(`Passwort für ${name} wirklich zurücksetzen?`)) return;
    try {
      const res = await adminResetPassword(userId);
      toast.success(`Neues Passwort: ${res.data.new_password}`);
      alert(`Neues Passwort für ${name}:\n\n${res.data.new_password}\n\nBitte an den Nutzer weitergeben.`);
    } catch (err) { toast.error('Fehler'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46]">Interessenten & Käufer</h1>
        <button onClick={() => { setShowCreate(!showCreate); setCreatedInfo(null); }} className="btn-primary flex items-center gap-2"><Plus size={16} /> Neuer Kontakt</button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="card-frost p-6 mb-6 animate-fade-in">
          <h3 className="font-heading text-lg text-[#2E3A46] mb-4">Kontakt manuell anlegen</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Name *</label>
              <input value={createForm.full_name} onChange={(e) => setCreateForm(f => ({...f, full_name: e.target.value}))}
                className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm" placeholder="Vor- und Nachname" /></div>
            <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">E-Mail *</label>
              <input type="email" value={createForm.email} onChange={(e) => setCreateForm(f => ({...f, email: e.target.value}))}
                className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm" placeholder="email@beispiel.de" /></div>
            <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Telefon</label>
              <input value={createForm.phone} onChange={(e) => setCreateForm(f => ({...f, phone: e.target.value}))}
                className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm" placeholder="+49..." /></div>
            <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Typ</label>
              <select value={createForm.buyer_type} onChange={(e) => setCreateForm(f => ({...f, buyer_type: e.target.value}))}
                className="w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm">
                <option value="interessent">Interessent</option><option value="kunde">Kunde</option><option value="kaeufer">Käufer</option>
              </select></div>
          </div>
          <button onClick={handleCreate} disabled={savingCreate} className="btn-primary text-sm flex items-center gap-1.5">
            <Save size={14} /> {savingCreate ? 'Erstellen...' : 'Kontakt anlegen'}
          </button>
          {createdInfo && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
              <p className="font-medium text-green-700">Kontakt erstellt!</p>
              <p className="text-green-600">Temporäres Passwort: <code className="bg-white px-2 py-0.5 rounded font-mono">{createdInfo.temp_password}</code></p>
              <p className="text-xs text-green-500 mt-1">Bitte an den Nutzer weitergeben. Er kann es nach dem ersten Login ändern.</p>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {buyers.map((buyer) => (
          <div key={buyer.id} className="card-frost p-5">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(expandedId === buyer.id ? null : buyer.id)}>
              <div>
                <h3 className="font-medium text-[#2E3A46]">{buyer.user_info?.full_name || buyer.user_id}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    buyer.buyer_type === 'kaeufer' ? 'bg-green-100 text-green-700' :
                    buyer.buyer_type === 'kunde' ? 'bg-[#C9A96E]/15 text-[#C9A96E]' :
                    'bg-[#7A9BB5]/15 text-[#7A9BB5]'
                  }`}>{buyer.buyer_type === 'kaeufer' ? 'Käufer' : buyer.buyer_type === 'kunde' ? 'Kunde' : 'Interessent'}</span>
                  <span className="text-xs text-[#7A9BB5]">{buyer.user_info?.email}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <ChevronDown size={16} className={`text-[#7A9BB5] transition-transform ${expandedId === buyer.id ? 'rotate-180' : ''}`} />
                <button onClick={(e) => { e.stopPropagation(); handleResetPw(buyer.user_id, buyer.user_info?.full_name || ''); }}
                  className="p-1.5 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5]" title="Passwort zurücksetzen">
                  <Settings size={14} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteUser(buyer.user_id); }}
                  className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Nutzer löschen">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {expandedId === buyer.id && (
              <div className="mt-4 pt-4 border-t border-[#C5D0DA]/50 animate-fade-in">
                <BuyerEditor buyer={buyer} onSave={(data) => handleSaveBuyer(buyer.user_id, data)} />
              </div>
            )}
          </div>
        ))}
        {buyers.length === 0 && (
          <div className="text-center py-12 text-[#7A9BB5]">
            <CreditCard size={48} className="mx-auto mb-3 opacity-50" />
            <p>Noch keine Käufer registriert</p>
          </div>
        )}
      </div>
    </div>
  );
};

const BuyerEditor = ({ buyer, onSave }) => {
  const [form, setForm] = useState({
    buyer_type: buyer?.buyer_type || 'interessent',
    deposit_paid: buyer?.deposit_paid || false, deposit_amount: buyer?.deposit_amount || 0,
    deposit_date: buyer?.deposit_date || '', balance_due: buyer?.balance_due || 0,
    pickup_date: buyer?.pickup_date || '', kitten_id: buyer?.kitten_id || '',
    admin_notes: buyer?.admin_notes || '',
    can_see_litters: buyer?.can_see_litters || false, can_see_contracts: buyer?.can_see_contracts || false, can_see_media: buyer?.can_see_media || false,
  });
  const [exclusiveMedia, setExclusiveMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [caption, setCaption] = useState('');

  useEffect(() => {
    if (buyer?.user_id) {
      import('../api').then(({ getBuyerMedia }) => {
        getBuyerMedia(buyer.user_id).then(r => setExclusiveMedia(r.data)).catch(() => {});
      });
    }
  }, [buyer?.user_id]);

  const handleMediaUpload = async (e) => {
    const files = e.target.files;
    if (!files.length || !buyer?.user_id) return;
    setUploading(true); setUploadProgress(0);
    try {
      const { uploadBuyerMedia } = await import('../api');
      for (let i = 0; i < files.length; i++) {
        const prog = (p) => setUploadProgress(Math.round(((i * 100) + p) / files.length));
        await uploadBuyerMedia(buyer.user_id, files[i], caption, prog);
      }
      toast.success(`${files.length} Datei(en) exklusiv bereitgestellt + E-Mail gesendet`);
      setCaption('');
      const { getBuyerMedia } = await import('../api');
      const r = await getBuyerMedia(buyer.user_id);
      setExclusiveMedia(r.data);
    } catch (err) { toast.error('Fehler beim Upload'); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const handleDeleteMedia = async (mediaId) => {
    try {
      const { deleteBuyerMedia } = await import('../api');
      await deleteBuyerMedia(mediaId);
      setExclusiveMedia(prev => prev.filter(m => m.id !== mediaId));
      toast.success('Medium gelöscht');
    } catch (err) { toast.error('Fehler'); }
  };

  return (
    <div className="space-y-4">
      {/* Buyer Type */}
      <div className="p-4 rounded-xl bg-[#D8E8F0] border border-[#C5D0DA]/50">
        <label className="block text-sm font-medium text-[#2E3A46] mb-2">Kundentyp</label>
        <div className="flex gap-2">
          {[{v:'interessent',l:'Interessent',c:'bg-[#7A9BB5]/15 text-[#7A9BB5]'},{v:'kunde',l:'Kunde',c:'bg-[#C9A96E]/15 text-[#C9A96E]'},{v:'kaeufer',l:'Käufer',c:'bg-green-100 text-green-700'}].map(t => (
            <button key={t.v} type="button" onClick={() => setForm(f => ({...f, buyer_type: t.v}))}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${form.buyer_type === t.v ? t.c + ' ring-2 ring-offset-1 ring-[#7A9BB5]' : 'bg-white text-[#7A9BB5] border border-[#C5D0DA]'}`}>
              {t.l}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-[#7A9BB5] mt-1">Interessent = allgemeines Interesse · Kunde = aktiv für Kitten · Käufer = hat bereits gekauft</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.deposit_paid} onChange={(e) => setForm(f => ({ ...f, deposit_paid: e.target.checked }))} className="w-4 h-4 accent-[#7A9BB5]" />
          Anzahlung erhalten
        </label>
        <div><label className="block text-xs text-[#7A9BB5] mb-1">Anzahlung (EUR)</label>
          <input type="number" value={form.deposit_amount} onChange={(e) => setForm(f => ({ ...f, deposit_amount: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-1.5 rounded-lg border border-[#C5D0DA] text-sm" /></div>
        <div><label className="block text-xs text-[#7A9BB5] mb-1">Anzahlungsdatum</label>
          <input type="date" value={form.deposit_date} onChange={(e) => setForm(f => ({ ...f, deposit_date: e.target.value }))} className="w-full px-3 py-1.5 rounded-lg border border-[#C5D0DA] text-sm" /></div>
        <div><label className="block text-xs text-[#7A9BB5] mb-1">Einzugstermin</label>
          <input type="date" value={form.pickup_date} onChange={(e) => setForm(f => ({ ...f, pickup_date: e.target.value }))} className="w-full px-3 py-1.5 rounded-lg border border-[#C5D0DA] text-sm" /></div>
      </div>

      {/* Permissions */}
      <div className="p-4 rounded-xl bg-[#EEF3F8] border border-[#C5D0DA]/50">
        <h4 className="text-sm font-medium text-[#2E3A46] mb-3">Freigaben</h4>
        <div className="flex flex-wrap gap-4">
          {[{key:'can_see_litters',label:'Würfe sehen'},{key:'can_see_contracts',label:'Verträge sehen'},{key:'can_see_media',label:'Medien sehen'}].map(p => (
            <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form[p.key]} onChange={(e) => setForm(f => ({ ...f, [p.key]: e.target.checked }))} className="w-4 h-4 accent-[#7A9BB5]" />
              {p.label}
            </label>
          ))}
        </div>
      </div>

      <div><label className="block text-xs text-[#7A9BB5] mb-1">Admin-Notizen (intern)</label>
        <textarea value={form.admin_notes} onChange={(e) => setForm(f => ({ ...f, admin_notes: e.target.value }))} rows={2} className="w-full px-3 py-1.5 rounded-lg border border-[#C5D0DA] text-sm" /></div>
      
      <button onClick={() => onSave(form)} className="btn-primary text-sm flex items-center gap-1.5"><Save size={14} /> Profil speichern</button>

      {/* Exclusive Media Section */}
      <div className="mt-4 pt-4 border-t border-[#C5D0DA]/50">
        <h4 className="font-heading text-base text-[#2E3A46] mb-2">Exklusive Fotos & Videos für diesen Kunden</h4>
        <p className="text-xs text-[#7A9BB5] mb-3">Diese Medien sind nur für diesen Kunden sichtbar. Bei Upload wird automatisch eine E-Mail-Benachrichtigung gesendet.</p>
        
        <div className="flex flex-wrap gap-2 mb-3">
          <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Bildunterschrift (optional)" className="flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border border-[#C5D0DA] text-sm" />
          <label className="btn-primary text-xs py-1.5 px-3 cursor-pointer flex items-center gap-1">
            <Upload size={12} /> {uploading ? `${uploadProgress}%` : 'Hochladen'}
            <input type="file" accept="image/*,video/*" multiple onChange={handleMediaUpload} className="hidden" />
          </label>
        </div>

        {uploading && (
          <div className="mb-3 p-2 rounded-lg bg-[#D8E8F0]">
            <div className="w-full h-1.5 bg-[#C5D0DA] rounded-full overflow-hidden">
              <div className="h-full bg-[#7A9BB5] rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          </div>
        )}

        {exclusiveMedia.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {exclusiveMedia.map(m => (
              <div key={m.id} className="relative rounded-lg overflow-hidden aspect-square group">
                {m.type === 'video' ? (
                  <video src={`${BACKEND_URL}${m.url}`} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={`${BACKEND_URL}${m.url}`} alt={m.caption || ''} className="w-full h-full object-cover" />
                )}
                {m.caption && <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] p-1 truncate">{m.caption}</div>}
                <button onClick={() => handleDeleteMedia(m.id)}
                  className="absolute top-1 right-1 p-1 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={10} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[#C5D0DA] italic">Noch keine exklusiven Medien für diesen Kunden.</p>
        )}
      </div>
    </div>
  );
};

// ===== ANNOUNCEMENTS =====
const AdminAnnouncements = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const loadData = () => { getAnnouncements().then(r => setItems(r.data)).catch(console.error); };
  useEffect(() => { loadData(); }, []);

  const handleSave = async () => {
    if (!form.title) { toast.error('Titel erforderlich'); return; }
    setSaving(true);
    try {
      if (editItem?.id) {
        await updateAnnouncement(editItem.id, form);
        toast.success('Neuigkeit aktualisiert');
      } else {
        await createAnnouncement(form);
        toast.success('Neuigkeit erstellt');
      }
      setShowForm(false); setEditItem(null); setForm({ title: '', body: '', is_active: true }); loadData();
    } catch (err) { toast.error('Fehler'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Neuigkeit löschen?')) return;
    try { await deleteAnnouncement(id); toast.success('Gelöscht'); loadData(); }
    catch (err) { toast.error('Fehler'); }
  };

  const handleMediaUpload = async (annId, e) => {
    const files = e.target.files;
    if (!files.length) return;
    setUploading(true); setUploadProgress(0);
    try {
      for (let i = 0; i < files.length; i++) {
        const fileProgress = (p) => setUploadProgress(Math.round(((i * 100) + p) / files.length));
        await uploadAnnouncementMedia(annId, files[i], fileProgress);
      }
      toast.success('Medien hochgeladen'); loadData();
    } catch (err) { toast.error('Fehler'); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46]">Neuigkeiten & Infos</h1>
        <button onClick={() => { setEditItem(null); setForm({ title: '', body: '', is_active: true }); setShowForm(true); }} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Neue Nachricht
        </button>
      </div>
      <p className="text-sm text-[#7A9BB5] mb-6">Erstellen Sie Neuigkeiten die auf der Landingpage angezeigt werden. Bilder und Videos können nach dem Erstellen hinzugefügt werden.</p>

      {showForm && (
        <div className="card-frost p-6 mb-6 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-[#2E3A46]">{editItem ? 'Bearbeiten' : 'Neue Nachricht'}</h3>
            <button onClick={() => setShowForm(false)}><X size={18} /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1">Titel *</label>
              <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2E3A46] mb-1">Inhalt</label>
              <textarea value={form.body} onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))} rows={4}
                className="w-full px-4 py-2.5 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] focus:border-[#7A9BB5] focus:outline-none" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-[#7A9BB5]" />
              Aktiv (auf Landingpage sichtbar)
            </label>
            <button onClick={handleSave} disabled={saving} className="btn-primary text-sm flex items-center gap-1.5">
              <Save size={14} /> {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>
      )}

      {uploading && (
        <div className="mb-4 p-3 rounded-xl bg-[#D8E8F0]">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-[#2E3A46]">Medien hochladen & komprimieren...</span>
            <span className="text-sm font-bold text-[#7A9BB5]">{uploadProgress}%</span>
          </div>
          <div className="w-full h-2 bg-[#C5D0DA] rounded-full overflow-hidden">
            <div className="h-full bg-[#7A9BB5] rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {items.map(item => (
          <div key={item.id} className={`card-frost p-5 ${!item.is_active ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-lg text-[#2E3A46]">{item.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-[#C5D0DA]/30 text-[#7A9BB5]'}`}>
                    {item.is_active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
                <p className="text-xs text-[#7A9BB5] mt-1">{new Date(item.created_at).toLocaleDateString('de-DE')}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => { setEditItem(item); setForm({ title: item.title, body: item.body, is_active: item.is_active }); setShowForm(true); }}
                  className="p-1.5 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5]"><Edit size={14} /></button>
                <label className="p-1.5 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5] cursor-pointer" title="Bild/Video hinzufügen">
                  <Upload size={14} />
                  <input type="file" accept="image/*,video/*" multiple onChange={(e) => handleMediaUpload(item.id, e)} className="hidden" />
                </label>
                <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
            {item.body && <p className="text-sm text-[#354551] mb-3">{item.body}</p>}
            {(item.images?.length > 0 || item.videos?.length > 0) && (
              <div className="flex flex-wrap gap-2">
                {item.images?.map((url, i) => (
                  <img key={i} src={`${BACKEND_URL}${url}`} alt="" className="w-20 h-20 rounded-lg object-cover" />
                ))}
                {item.videos?.map((url, i) => (
                  <div key={`v${i}`} className="w-20 h-20 rounded-lg bg-[#2E3A46] flex items-center justify-center relative overflow-hidden">
                    <video src={`${BACKEND_URL}${url}`} className="w-full h-full object-cover" muted />
                    <span className="absolute text-white text-xs bg-black/60 px-1 rounded">Video</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center py-12 text-[#7A9BB5]">
            <Megaphone size={48} className="mx-auto mb-3 opacity-50" /><p>Noch keine Neuigkeiten erstellt</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== CONTRACTS =====
const AdminContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = () => {
    Promise.all([getContracts(), getBuyers(), getAnimals({admin_view:true})]).then(([c,b,a]) => {
      setContracts(c.data); setBuyers(b.data); setAnimals(a.data);
    }).catch(console.error);
  };
  useEffect(() => { loadData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Vertrag wirklich löschen?')) return;
    try { await deleteContract(id); toast.success('Vertrag gelöscht'); loadData(); }
    catch (err) { toast.error('Fehler'); }
  };

  const handleApprove = async (id) => {
    try { await approveContract(id); toast.success('Vertrag freigegeben'); loadData(); }
    catch (err) { toast.error('Fehler'); }
  };

  const handleAdminSign = async (id) => {
    const name = prompt('Ihr Name für die Gegenzeichnung:');
    if (!name) return;
    try { await adminSignContract(id, {signed_name: name}); toast.success('Gegenzeichnung gespeichert'); loadData(); }
    catch (err) { toast.error('Fehler'); }
  };

  const handleDownloadPdf = async (id) => {
    try {
      const { getContractPDF } = await import('../api');
      const res = await getContractPDF(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'vertrag_' + id.substring(0,8) + '.pdf'; a.click();
    } catch (err) { toast.error('PDF-Fehler'); }
  };

  const typeLabels = {purchase:'Kaufvertrag',deposit:'Anzahlungsbestätigung',breeding_ban:'Zuchtverbot',photo_consent:'Fotoeinwilligung'};
  const statusLabels = {draft:'Entwurf',approved:'Freigegeben',signed:'Unterschrieben'};
  const statusColors = {draft:'bg-[#C5D0DA]/30 text-[#7A9BB5]',approved:'bg-[#C9A96E]/15 text-[#C9A96E]',signed:'bg-green-100 text-green-700'};

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46]">Verträge</h1>
        <button onClick={() => { setEditContract(null); setShowForm(true); }} className="btn-primary flex items-center gap-2"><Plus size={16} /> Neuer Vertrag</button>
      </div>

      {showForm && <ContractForm contract={editContract} buyers={buyers} animals={animals} onClose={() => { setShowForm(false); setEditContract(null); }} onSaved={() => { setShowForm(false); setEditContract(null); loadData(); }} />}

      <div className="space-y-3">
        {contracts.map(c => (
          <div key={c.id} className="card-frost p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-[#2E3A46]">{typeLabels[c.type]||c.type}</h3>
                  <span className={"text-xs px-2 py-0.5 rounded-full " + (statusColors[c.status]||'')}>{statusLabels[c.status]||c.status}</span>
                </div>
                <p className="text-xs text-[#7A9BB5]">{c.buyer_name || 'Käufer'} · {c.cat_name || 'Katze'} · {new Date(c.created_at).toLocaleDateString('de-DE')}</p>
                {c.admin_signed_name && <p className="text-xs text-green-600 mt-1">Gegengezeichnet: {c.admin_signed_name}</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                {c.status === 'draft' && <>
                  <button onClick={() => { setEditContract(c); setShowForm(true); }} className="p-1.5 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5]" title="Bearbeiten"><Edit size={14} /></button>
                  <button onClick={() => handleApprove(c.id)} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600" title="Freigeben"><Check size={14} /></button>
                </>}
                {c.status === 'approved' && !c.admin_signed_name && <button onClick={() => handleAdminSign(c.id)} className="p-1.5 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5]" title="Gegenzeichnen"><Edit size={14} /></button>}
                <button onClick={() => handleDownloadPdf(c.id)} className="p-1.5 rounded-lg hover:bg-[#D8E8F0] text-[#7A9BB5]" title="PDF"><FileText size={14} /></button>
                {c.status !== 'signed' && <button onClick={() => handleDelete(c.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Löschen"><Trash2 size={14} /></button>}
              </div>
            </div>
          </div>
        ))}
        {contracts.length === 0 && <div className="text-center py-12 text-[#7A9BB5]"><FileText size={48} className="mx-auto mb-3 opacity-50" /><p>Noch keine Verträge</p></div>}
      </div>
    </div>
  );
};

const ContractForm = ({ contract, buyers, animals, onClose, onSaved }) => {
  const [form, setForm] = useState({
    buyer_id: contract?.buyer_id || '', type: contract?.type || 'purchase',
    buyer_name: contract?.buyer_name || '', buyer_address: contract?.buyer_address || '',
    buyer_email: contract?.buyer_email || '', buyer_phone: contract?.buyer_phone || '', buyer_id_number: contract?.buyer_id_number || '',
    cat_name: contract?.cat_name || '', cat_birth_date: contract?.cat_birth_date || '', cat_color: contract?.cat_color || '',
    cat_father: contract?.cat_father || '', cat_mother: contract?.cat_mother || '', cat_studbook_nr: contract?.cat_studbook_nr || '',
    price_amount: contract?.price_amount || 0, price_words: contract?.price_words || '',
    deposit_amount: contract?.deposit_amount || 0, payment_reference: contract?.payment_reference || '',
    handover_date: contract?.handover_date || '', breeding_permission: contract?.breeding_permission || false,
    special_agreements: contract?.special_agreements || '', castration_clause: contract?.castration_clause || '',
    breeding_clause: contract?.breeding_clause || '', custom_text: contract?.custom_text || '',
    para_3: contract?.para_3 || 'Bis zur vollständigen Bezahlung bleiben das Tier und die dazugehörigen Papiere Eigentum des Züchters.',
    para_4: contract?.para_4 || 'Der Züchter versichert, dass das Tier zum Zeitpunkt der Übergabe gesund und frei von sichtbaren ansteckenden Krankheiten ist. Das Tier ist stubenrein, entwöhnt und frei von Ungeziefer.\n\nDer Züchter haftet nicht für versteckte Mängel und Krankheiten. Ebenfalls haftet der Züchter nicht für ein Tier, welches an einer FIP erkranken sollte. Eine erfolgreiche Behandlung mit GS 441524 ist möglich.\n\nDie Katze wird verkauft wie besichtigt. Spätere Ansprüche sind ausgeschlossen. Impfungen und Chip werden individuell geplant.',
    para_5: contract?.para_5 || 'a) Kein Zwischenkauf. b) Artgerechte Haltung. c) Keine Abgabe an Tierheime. d) Ableben melden. e) Weiterveräußerung anzeigen (Vorkaufsrecht). f) Keine fremdrassige Verpaarung. g) Zucht nur mit Vereinsmitgliedschaft. h) Nicht potent verkaufen. i) Vertragsstrafe bis 1.000 EUR.\n\nVorkaufsrecht: 2 Wochen Frist, Rückerstattung bis 1/3.',
    para_6: contract?.para_6 || 'Besondere Absprachen, Ergänzungen und Änderungen des Vertrags bedürfen der Schriftform.',
    para_7: contract?.para_7 || 'Bei Streitigkeiten ist Gerichtsstand der Wohnort des Züchters. Sollte ein Vertragspunkt unwirksam sein, bleiben die übrigen bindend.',
    para_8_footer: contract?.para_8_footer || 'Der Züchter hat den Käufer über maternale Antikörper aufgeklärt. Der Züchter versichert, dass es keine Calici, Herpes oder Katzenseuche in der Zucht gab.\n\nMit seiner Unterschrift erklärt der Käufer, dass er den Vertrag gelesen und verstanden hat.',
  });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('buyer');
  const ch = (e) => { const {name,value,type,checked} = e.target; setForm(f => ({...f, [name]: type==='checkbox'?checked:value})); };

  // Auto-fill buyer data
  const fillBuyer = (buyerId) => {
    const b = buyers.find(x => x.id === buyerId);
    if (b?.user_info) setForm(f => ({...f, buyer_id: buyerId, buyer_name: b.user_info.full_name||'', buyer_email: b.user_info.email||'', buyer_phone: b.user_info.phone||''}));
  };

  const handleSave = async () => {
    if (!form.buyer_name) { toast.error('Käufername erforderlich'); return; }
    setSaving(true);
    try {
      if (contract?.id) { await updateContractFull(contract.id, form); toast.success('Vertrag aktualisiert'); }
      else { await createContract(form); toast.success('Vertrag erstellt'); }
      onSaved();
    } catch (err) { toast.error('Fehler'); } finally { setSaving(false); }
  };

  const tabs = [{id:'buyer',label:'Käufer'},{id:'cat',label:'Katze'},{id:'price',label:'Preis & Zahlung'},{id:'clauses',label:'Vertragstexte §§3-7'},{id:'special',label:'§8 Sondervereinbarungen'},{id:'preview',label:'Vorschau'}];
  const inp = "w-full px-3 py-2 rounded-xl border border-[#C5D0DA] bg-white text-sm focus:border-[#7A9BB5] focus:outline-none";

  return (
    <div className="card-frost p-6 mb-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl text-[#2E3A46]">{contract?.id ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}</h3>
        <button onClick={onClose}><X size={18} /></button>
      </div>
      <div className="flex overflow-x-auto gap-1 mb-6 pb-1 scrollbar-hide">
        {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} className={"px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap " + (tab===t.id ? "bg-[#2E3A46] text-white" : "bg-[#EEF3F8] text-[#7A9BB5] hover:bg-[#D8E8F0]")}>{t.label}</button>)}
      </div>

      {tab === 'buyer' && <div className="space-y-3">
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Käufer auswählen</label>
          <select onChange={(e) => fillBuyer(e.target.value)} className={inp}><option value="">Aus Liste wählen...</option>
            {buyers.map(b => <option key={b.id} value={b.id}>{b.user_info?.full_name} ({b.user_info?.email})</option>)}</select></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Name *</label><input name="buyer_name" value={form.buyer_name} onChange={ch} className={inp} /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Adresse</label><input name="buyer_address" value={form.buyer_address} onChange={ch} className={inp} /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">E-Mail</label><input name="buyer_email" value={form.buyer_email} onChange={ch} className={inp} /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Telefon</label><input name="buyer_phone" value={form.buyer_phone} onChange={ch} className={inp} /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Ausweis-Nr.</label><input name="buyer_id_number" value={form.buyer_id_number} onChange={ch} className={inp} /></div>
        </div>
      </div>}

      {tab === 'cat' && <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Katzenname</label><input name="cat_name" value={form.cat_name} onChange={ch} className={inp} placeholder="z.B. Beverly von den Schneeblumen" /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Geburtsdatum</label><input name="cat_birth_date" type="date" value={form.cat_birth_date} onChange={ch} className={inp} /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Farbe</label><input name="cat_color" value={form.cat_color} onChange={ch} className={inp} placeholder="z.B. chocolate point" /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Zuchtbuch-Nr.</label><input name="cat_studbook_nr" value={form.cat_studbook_nr} onChange={ch} className={inp} /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Vater</label><input name="cat_father" value={form.cat_father} onChange={ch} className={inp} /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Mutter</label><input name="cat_mother" value={form.cat_mother} onChange={ch} className={inp} /></div>
        </div>
      </div>}

      {tab === 'price' && <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Kaufpreis (EUR)</label><input name="price_amount" type="number" value={form.price_amount} onChange={ch} className={inp} /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Preis in Worten</label><input name="price_words" value={form.price_words} onChange={ch} className={inp} placeholder="z.B. eintausendfünfhundert" /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Anzahlung (EUR)</label><input name="deposit_amount" type="number" value={form.deposit_amount} onChange={ch} className={inp} /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Verwendungszweck</label><input name="payment_reference" value={form.payment_reference} onChange={ch} className={inp} /></div>
          <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Übergabedatum</label><input name="handover_date" type="date" value={form.handover_date} onChange={ch} className={inp} /></div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" name="breeding_permission" checked={form.breeding_permission} onChange={ch} className="w-4 h-4 accent-[#7A9BB5]" /> Mit Zuchterlaubnis</label>
      </div>}

      {tab === 'clauses' && <div className="space-y-4">
        <p className="text-xs text-[#7A9BB5]">Alle Vertragstexte sind frei änderbar. Änderungen gelten nur für diesen Vertrag.</p>
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">§ 3: Eigentumsvorbehalt</label>
          <textarea name="para_3" value={form.para_3} onChange={ch} rows={2} className={inp} /></div>
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">§ 4: Gewährleistung u.a.</label>
          <textarea name="para_4" value={form.para_4} onChange={ch} rows={6} className={inp} /></div>
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">§ 5: Nebenpflichten des Käufers, Vertragsstrafe</label>
          <textarea name="para_5" value={form.para_5} onChange={ch} rows={6} className={inp} /></div>
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">§ 6: Schriftform</label>
          <textarea name="para_6" value={form.para_6} onChange={ch} rows={2} className={inp} /></div>
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">§ 7: Gerichtsstand</label>
          <textarea name="para_7" value={form.para_7} onChange={ch} rows={2} className={inp} /></div>
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">§ 8: Schlusstext (nach Sondervereinbarungen)</label>
          <textarea name="para_8_footer" value={form.para_8_footer} onChange={ch} rows={3} className={inp} /></div>
      </div>}

      {tab === 'special' && <div className="space-y-3">
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Kastrations-Klausel</label><textarea name="castration_clause" value={form.castration_clause} onChange={ch} rows={2} className={inp} placeholder="z.B. Kastrationspflicht bis zum 6. Lebensmonat" /></div>
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Zucht-Klausel</label><textarea name="breeding_clause" value={form.breeding_clause} onChange={ch} rows={2} className={inp} placeholder="z.B. Zuchtverbot / Zuchterlaubnis Bedingungen" /></div>
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Weitere Sondervereinbarungen</label><textarea name="special_agreements" value={form.special_agreements} onChange={ch} rows={4} className={inp} placeholder="Individuelle Vereinbarungen..." /></div>
        <div><label className="block text-xs font-medium text-[#2E3A46] mb-1">Zusätzlicher Freitext</label><textarea name="custom_text" value={form.custom_text} onChange={ch} rows={3} className={inp} placeholder="Weiterer Text für den Vertrag..." /></div>
      </div>}

      {tab === 'preview' && <div className="bg-white border border-[#C5D0DA] rounded-xl p-5 text-[11px] max-h-[600px] overflow-y-auto leading-relaxed">
        <div className="text-center mb-4"><img src="/contract_banner.png" alt="" className="w-full object-contain" /></div>
        <h2 className="text-center font-heading text-xl mb-1">KAUFVERTRAG</h2>
        <p className="text-center text-xs text-[#7A9BB5] mb-4">Birma von den Schneeblumen · Heilige Birma Katzenzucht</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 bg-[#EEF3F8] rounded-lg"><p className="font-bold mb-1 text-xs">Züchter:</p><p>Dorothea Schulz<br/>Royberg 2c, 21271 Hanstedt<br/>Tel: 0176 311 34 989<br/>www.birma-schneeblumen.de<br/>doro@birma-schneeblumen.de</p></div>
          <div className="p-3 bg-[#EEF3F8] rounded-lg"><p className="font-bold mb-1 text-xs">Käufer:</p><p>{form.buyer_name||'___'}<br/>{form.buyer_address||'___'}<br/>Tel: {form.buyer_phone||'___'}<br/>E-Mail: {form.buyer_email||'___'}</p></div>
        </div>

        <div className="p-3 bg-[#D8E8F0] rounded-lg mb-3"><p className="font-bold text-xs mb-1">§ 1: Kaufgegenstand</p>
          <p>wird der folgende Vertrag geschlossen. Der Käufer erwirbt die nachfolgend beschriebenen Katzen:</p>
          <p className="mt-1"><strong>Name:</strong> {form.cat_name||'___'} · <strong>Geburtsdatum:</strong> {form.cat_birth_date||'___'} · <strong>Rasse:</strong> Heilige Birma<br/><strong>Farbe:</strong> {form.cat_color||'___'} · <strong>Vater:</strong> {form.cat_father||'___'} · <strong>Mutter:</strong> {form.cat_mother||'___'}<br/><strong>Zuchtbuch-Nr:</strong> {form.cat_studbook_nr||'___'}</p>
        </div>

        <div className="p-3 bg-[#EEF3F8] rounded-lg mb-3"><p className="font-bold text-xs mb-1">§ 2: Kaufpreis, Übergabe, Rücktrittsfolgen</p>
          <p>Der Kaufpreis für 1 Jungtier <strong>{form.breeding_permission ? 'mit Zuchterlaubnis' : 'ohne Zuchterlaubnis'}</strong> beträgt Euro: <strong>{form.price_amount||'___'} Euro</strong> i.W: {form.price_words||'___'}</p>
          <p className="mt-1">Wenn das Tier fest reserviert werden möchte, fällt eine Anzahlung von ca. 1/3 des Kaufpreises an. Diese wird auf folgendes Konto überwiesen und beträgt = <strong>{form.deposit_amount || Math.round((form.price_amount||0)/3)} Euro</strong></p>
          <p className="mt-1">Dorothea Schulz · VR Bank<br/>IBAN: DE48 2139 0008 0007 8048 14 · BIC: GENODEF1NSH<br/>Verwendungszweck: {form.payment_reference||'___'}<br/>Paypal: sweetlily@gmx.de (bitte auf Zahlung als Freunde achten)</p>
          <p className="mt-1">Der Übergabetermin ist voraussichtlich der <strong>{form.handover_date||'___'}</strong>.<br/>Der komplette Betrag/Differenzbetrag des Kaufpreises ist bei Übergabe vollständig zu zahlen.</p>
        </div>

        <div className="p-3 border border-[#C5D0DA] rounded-lg mb-3"><p className="font-bold text-xs mb-1">§ 3: Eigentumsvorbehalt</p>
          <p className="whitespace-pre-line">{form.para_3}</p></div>

        <div className="p-3 border border-[#C5D0DA] rounded-lg mb-3"><p className="font-bold text-xs mb-1">§ 4: Gewährleistung u.a.</p>
          <p className="whitespace-pre-line">{(form.para_4 || '').replace(/dem Tier/g, form.cat_name || 'dem Tier')}</p></div>

        <div className="p-3 border border-[#C5D0DA] rounded-lg mb-3"><p className="font-bold text-xs mb-1">§ 5: Nebenpflichten des Käufers, Vertragsstrafe</p>
          <p className="whitespace-pre-line">{(form.para_5 || '').replace(/das Tier/g, form.cat_name || 'das Tier')}</p></div>

        <div className="p-3 border border-[#C5D0DA] rounded-lg mb-3"><p className="font-bold text-xs mb-1">§ 6: Schriftform</p>
          <p className="whitespace-pre-line">{form.para_6}</p></div>

        <div className="p-3 border border-[#C5D0DA] rounded-lg mb-3"><p className="font-bold text-xs mb-1">§ 7: Gerichtsstand u.a.</p>
          <p className="whitespace-pre-line">{form.para_7}</p></div>

        <div className="p-3 bg-[#C9A96E]/10 border-l-3 border-[#C9A96E] rounded-lg mb-3"><p className="font-bold text-xs mb-1">§ 8: Besondere Bemerkungen, Zusatzvereinbarungen</p>
          {form.castration_clause && <p><strong>Kastration:</strong> {form.castration_clause}</p>}
          {form.breeding_clause && <p><strong>Zucht:</strong> {form.breeding_clause}</p>}
          {form.special_agreements && <p>{form.special_agreements}</p>}
          <p className="mt-1 whitespace-pre-line">{form.para_8_footer}</p>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-6 text-xs text-[#354551]">
          <div><p>Ort, Datum: ________________</p><div className="border-b border-[#2E3A46] mb-1 mt-8"></div><p className="text-[#7A9BB5]">Unterschrift Züchter (Dorothea Schulz)</p></div>
          <div><p>Ort, Datum: ________________</p><div className="border-b border-[#2E3A46] mb-1 mt-8"></div><p className="text-[#7A9BB5]">Unterschrift Käufer ({form.buyer_name||'___'})</p></div>
        </div>
      </div>}

      <div className="flex gap-3 mt-4">
        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2"><Save size={16} /> {saving ? 'Speichern...' : 'Vertrag speichern'}</button>
        <button onClick={onClose} className="btn-outline">Abbrechen</button>
      </div>
    </div>
  );
};


// ===== DASHBOARD PREVIEW =====
const AdminDashboardPreview = () => {
  const [buyers, setBuyers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getBuyers().then(res => setBuyers(res.data)).catch(console.error);
  }, []);

  const loadPreview = async (userId) => {
    setLoading(true);
    try {
      const { previewBuyerDashboard } = await import('../api');
      const res = await previewBuyerDashboard(userId);
      setPreviewData(res.data);
      setSelectedUser(userId);
    } catch (err) { toast.error('Vorschau konnte nicht geladen werden'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h1 className="font-heading text-2xl md:text-3xl text-[#2E3A46] mb-6">Dashboard-Vorschau</h1>
      <p className="text-[#7A9BB5] text-sm mb-6">Wählen Sie einen Käufer, um dessen Dashboard-Ansicht zu sehen.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User list */}
        <div className="space-y-2">
          {buyers.map(buyer => (
            <button key={buyer.id} onClick={() => loadPreview(buyer.user_id)}
              className={`w-full text-left p-3 rounded-xl border transition-colors ${selectedUser === buyer.user_id ? 'border-[#7A9BB5] bg-[#D8E8F0]' : 'border-[#C5D0DA] bg-white hover:bg-[#EEF3F8]'}`}>
              <p className="font-medium text-[#2E3A46] text-sm">{buyer.user_info?.full_name || 'Unbenannt'}</p>
              <p className="text-xs text-[#7A9BB5]">{buyer.user_info?.email}</p>
              <div className="flex gap-2 mt-1">
                {buyer.deposit_paid && <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">Anzahlung</span>}
                {buyer.can_see_litters && <span className="text-xs px-1.5 py-0.5 rounded bg-[#7A9BB5]/15 text-[#7A9BB5]">Würfe</span>}
              </div>
            </button>
          ))}
          {buyers.length === 0 && <p className="text-sm text-[#7A9BB5] text-center py-8">Noch keine Käufer registriert</p>}
        </div>

        {/* Preview */}
        <div className="lg:col-span-2">
          {loading ? (
            <div className="text-center py-12"><div className="w-8 h-8 border-3 border-[#C5D0DA] border-t-[#7A9BB5] rounded-full animate-spin mx-auto"></div></div>
          ) : previewData ? (
            <div className="card-frost p-6 border-2 border-dashed border-[#7A9BB5]">
              <div className="flex items-center gap-2 mb-4">
                <Monitor size={18} className="text-[#7A9BB5]" />
                <span className="text-xs text-[#7A9BB5] font-medium uppercase tracking-wider">Dashboard-Vorschau für: {previewData.user?.full_name}</span>
              </div>

              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-[#EEF3F8]">
                  <h3 className="font-heading text-lg text-[#2E3A46] mb-2">Willkommen, {previewData.user?.full_name}!</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-[#7A9BB5]">Wissenskapitel:</span> <strong>{previewData.knowledge_count}</strong></div>
                    <div><span className="text-[#7A9BB5]">Anzahlung:</span> <strong>{previewData.has_deposit ? 'Ja' : 'Nein'}</strong></div>
                    <div><span className="text-[#7A9BB5]">Würfe sichtbar:</span> <strong>{previewData.can_see_litters ? 'Ja' : 'Nein'}</strong></div>
                    <div><span className="text-[#7A9BB5]">Verträge:</span> <strong>{previewData.contracts?.length || 0}</strong></div>
                  </div>
                </div>

                {previewData.kitten && (
                  <div className="p-4 rounded-xl bg-[#D8E8F0]">
                    <h4 className="font-medium text-[#2E3A46] mb-1">Zugewiesenes Kitten</h4>
                    <p className="text-sm text-[#354551]">{previewData.kitten.name} · {previewData.kitten.color}</p>
                  </div>
                )}

                {previewData.litters?.length > 0 && (
                  <div className="p-4 rounded-xl bg-[#E8EEF3]">
                    <h4 className="font-medium text-[#2E3A46] mb-1">Sichtbare Würfe</h4>
                    {previewData.litters.map(l => (
                      <p key={l.id} className="text-sm text-[#354551]">{l.name} ({l.kittens?.length || 0} Kitten)</p>
                    ))}
                  </div>
                )}

                <div className="p-4 rounded-xl bg-[#EEF3F8]">
                  <h4 className="font-medium text-[#2E3A46] mb-1">Freigaben</h4>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className={`px-2 py-1 rounded ${previewData.buyer?.can_see_litters ? 'bg-green-100 text-green-700' : 'bg-[#C5D0DA]/30 text-[#7A9BB5]'}`}>Würfe: {previewData.buyer?.can_see_litters ? 'Ja' : 'Nein'}</span>
                    <span className={`px-2 py-1 rounded ${previewData.buyer?.can_see_contracts ? 'bg-green-100 text-green-700' : 'bg-[#C5D0DA]/30 text-[#7A9BB5]'}`}>Verträge: {previewData.buyer?.can_see_contracts ? 'Ja' : 'Nein'}</span>
                    <span className={`px-2 py-1 rounded ${previewData.buyer?.can_see_media ? 'bg-green-100 text-green-700' : 'bg-[#C5D0DA]/30 text-[#7A9BB5]'}`}>Medien: {previewData.buyer?.can_see_media ? 'Ja' : 'Nein'}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-16 text-[#7A9BB5]">
              <Monitor size={48} className="mx-auto mb-4 opacity-30" />
              <p>Wählen Sie einen Käufer aus der Liste</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
